import React, { useState, useEffect, useRef, useMemo, useCallback } from 'react';
import { FiSearch, FiMapPin, FiCheck, FiCamera, FiUpload, FiList, FiPlus, FiMinus, FiPrinter, FiPackage } from 'react-icons/fi';
import * as XLSX from 'xlsx';
import api from '../services/api';
import { useSearch } from '../contexts/SearchContext';
import Modal from '../components/Modal';
import BarcodeScanner from '../components/BarcodeScanner';
import SearchInputWithHistory from '../components/SearchInputWithHistory';
import { RETOUR_REDEN_OPTIES, RETOUR_REDEN_VALUE_ANDERS } from '../constants/retourenReden';
import '../components/Button.css';
import '../styles/ProductLabelPrint.css';
import { getBarcodeSpec, renderBarcodeOnSvg } from '../utils/productLabelPrint';
import './Magazijn.css';
import { ProductNaamMetKlasse } from '../components/ProductKlasseIcon';

const MAGAZIJN_SCAN_CONTEXT = 'magazijn_picklijst';
const MAGAZIJN_RETOUR_ONTVANGST = 'retour_ontvangst';

function retourKanNogVerwerktWorden(r) {
  return r.status !== 'geweigerd' && r.status !== 'terugbetaald' && r.refund_status !== 'succeeded';
}

function normalizePostcodeSearch(s) {
  return String(s || '').replace(/\s+/g, '').toLowerCase();
}

function postcodeDigits(s) {
  return normalizePostcodeSearch(s).replace(/\D/g, '').slice(0, 4);
}

function postcodeMatchesSearch(postcode, searchTerm) {
  const pcDigits = postcodeDigits(postcode);
  if (pcDigits.length < 4) return false;
  const qDigits = String(searchTerm || '').replace(/\D/g, '').slice(0, 4);
  if (qDigits.length >= 4) {
    return pcDigits === qDigits;
  }
  const qNorm = normalizePostcodeSearch(searchTerm);
  const pcNorm = normalizePostcodeSearch(postcode);
  return qNorm.length > 0 && pcNorm.includes(qNorm);
}

function retourIsTerugbetaald(r) {
  return (
    r?.status === 'terugbetaald' ||
    r?.refund_status === 'succeeded' ||
    r?.refund_status === 'bol_handmatig'
  );
}

function formatTeruglegLocaties(raw) {
  if (!raw) return '';
  return String(raw)
    .split(',')
    .map((x) => x.trim())
    .filter(Boolean)
    .join(' · ');
}

/** Alle aangemelde regels volledig in ontvangst (OK + defect = aangemeld). */
function isRetourOntvangstComplete(retour) {
  if (!retour?.regels?.length) return false;
  const active = retour.regels.filter((r) => (parseInt(r.aantal_aangemeld, 10) || 0) > 0);
  if (!active.length) return false;
  return active.every((r) => {
    const a = parseInt(r.aantal_aangemeld, 10) || 0;
    const okc = parseInt(r.aantal_ok, 10) || 0;
    const dfc = parseInt(r.aantal_defect, 10) || 0;
    return okc + dfc >= a;
  });
}

/** Minstens één ontvangen stuk; gedeeltelijke retour mag terugbetaald worden. */
function isRetourKlaarVoorTerugbetaling(retour) {
  if (!retour?.regels?.length) return false;
  let any = false;
  for (const r of retour.regels) {
    const a = parseInt(r.aantal_aangemeld, 10) || 0;
    const ontv = (parseInt(r.aantal_ok, 10) || 0) + (parseInt(r.aantal_defect, 10) || 0);
    if (ontv > a) return false;
    if (a > 0 && ontv > 0) any = true;
  }
  return any;
}

function picklijstItemOpenPickAantal(item) {
  if (item?.verzonden_volledig) return 0;
  if (item?.resterend != null) return Math.max(0, parseInt(item.resterend, 10) || 0);
  const tot = parseInt(item?.aantal, 10) || 0;
  const shipped = parseInt(item?.shipped_qty, 10) || 0;
  return Math.max(0, tot - shipped);
}

/** Regel is al (deels) verzonden en hoeft niet meer gescand te worden. */
function picklijstItemMoetNogGepicktWorden(item) {
  if (item?.verzonden_volledig) return false;
  return picklijstItemOpenPickAantal(item) > 0;
}

/**
 * Picklijst-sectie "met voorraad" vs "geen voorraad".
 * Woo kan bij backorder een negatieve stock_quantity tonen; de order staat wél in processing en moet pickbaar blijven.
 */
function picklijstItemHasPickbareVoorraad(item) {
  const needMain = picklijstItemOpenPickAantal(item) || parseInt(item?.aantal, 10) || 0;
  // Voor een bundel: pickbaar als alle onderdelen (per benodigd aantal) leverbaar zijn.
  if (item && item.is_bundel && Array.isArray(item.onderdelen) && item.onderdelen.length > 0) {
    return item.onderdelen.every((child) => {
      const v = child.voorraad;
      const need = child.aantal || 0;
      if (v == null || v === undefined) return true;
      if (v < 0) return true;
      return v >= need;
    });
  }
  const v = item.voorraad;
  const need = needMain;
  if (v == null || v === undefined) return true;
  if (v < 0) return true;
  return v >= need;
}

/** Orderregel uit orders-in-behandeling (zelfde velden als picklijst-item). */
function orderRegelHasPickbareVoorraad(regel) {
  return picklijstItemHasPickbareVoorraad(regel);
}

function orderRegelRestAantal(regel) {
  if (regel?.verzonden_volledig) return 0;
  if (regel?.resterend != null) return Math.max(0, parseInt(regel.resterend, 10) || 0);
  const tot = parseInt(regel?.aantal, 10) || 0;
  const shipped = parseInt(regel?.shipped_qty, 10) || 0;
  return Math.max(0, tot - shipped);
}

/** Regel hoort bij deze (deel)zending — niet backorder / geen voorraad. */
function orderRegelOpenVoorShipNow(regel) {
  if (orderRegelRestAantal(regel) <= 0) return false;
  if (!orderRegelHasPickbareVoorraad(regel)) return false;
  if (regel.voorraad != null && regel.voorraad < 0) return false;
  return true;
}

function picklijstItemOpenVoorShipNow(item) {
  if (picklijstItemOpenPickAantal(item) <= 0) return false;
  if (!picklijstItemHasPickbareVoorraad(item)) return false;
  if (item.voorraad != null && item.voorraad < 0) return false;
  return true;
}

function picklijstCanPartialShip(items) {
  const list = items || [];
  const open = list.filter((i) => picklijstItemOpenPickAantal(i) > 0);
  const shipNow = open.filter(picklijstItemOpenVoorShipNow);
  const later = open.filter((i) => !picklijstItemOpenVoorShipNow(i));
  return shipNow.length > 0 && later.length > 0;
}

/** Welke picklijstregels moeten gescand zijn vóór verzenden? */
function picklijstItemsRequiredBeforeShip(items) {
  const list = items || [];
  if (picklijstCanPartialShip(list)) {
    return list.filter(picklijstItemOpenVoorShipNow);
  }
  return list.filter(picklijstItemNeedsPicking);
}

/** Picklijst-item: al volledig verzonden telt niet meer mee voor scan/ship. */
function picklijstItemNeedsPicking(item) {
  if (!picklijstItemMoetNogGepicktWorden(item)) return false;
  return picklijstItemHasPickbareVoorraad(item);
}

/** Order heeft open regels voor nu én later → deelzending mogelijk. */
function orderHasDeelzending(order) {
  const regels = order?.regels || [];
  const openAll = regels.filter((r) => orderRegelRestAantal(r) > 0);
  if (openAll.length === 0) return false;
  const shipNow = openAll.filter(orderRegelOpenVoorShipNow);
  const later = openAll.filter((r) => !orderRegelOpenVoorShipNow(r));
  return shipNow.length > 0 && later.length > 0;
}

function buildShippedLineItemsForOrder(order) {
  const regels = order?.regels || [];
  const openAll = regels.filter((r) => orderRegelRestAantal(r) > 0);
  const shipNow = openAll.filter(orderRegelOpenVoorShipNow);
  const hasLater = openAll.some((r) => !orderRegelOpenVoorShipNow(r));
  const source = hasLater ? shipNow : openAll;
  return source
    .map((r) => ({
      line_item_id: parseInt(r.wc_line_item_id, 10),
      quantity: orderRegelRestAantal(r),
    }))
    .filter((row) => row.line_item_id > 0 && row.quantity > 0);
}

/** Blijft er na deze zending nog iets open op de order? */
function computePartialAfterShipment(order, shippedLineItems) {
  if (!order) return false;
  const shippedMap = new Map(
    (shippedLineItems || []).map((s) => [parseInt(s.line_item_id, 10), parseInt(s.quantity, 10) || 0])
  );
  for (const r of order.regels || []) {
    const rest = orderRegelRestAantal(r);
    if (rest <= 0) continue;
    const id = parseInt(r.wc_line_item_id, 10);
    const shipNow = id ? shippedMap.get(id) || 0 : 0;
    if (rest - shipNow > 0) return true;
  }
  return false;
}

function findOrderInProductieByWooId(orders, orderId) {
  const target = toWooIdForPicklijst(orderId);
  if (!target) return null;
  return (orders || []).find((o) => toWooIdForPicklijst(o.id) === target) || null;
}

function buildPartialShipmentNote(order) {
  const regels = order?.regels || [];
  const shipped = regels.filter(orderRegelOpenVoorShipNow);
  const pending = regels.filter((r) => orderRegelRestAantal(r) > 0 && !orderRegelOpenVoorShipNow(r));
  const already = regels.filter((r) => (parseInt(r.shipped_qty, 10) || 0) > 0);
  const lines = ['Deelzending verzonden via Magazijn.'];
  if (shipped.length) {
    lines.push('Verzonden (voorraad beschikbaar):');
    for (const r of shipped) {
      lines.push(`- ${orderRegelRestAantal(r)}× ${r.product_naam || r.sku || 'Product'}`);
    }
  }
  if (pending.length) {
    lines.push('Nog open (wacht op voorraad):');
    for (const r of pending) {
      lines.push(`- ${orderRegelRestAantal(r)}× ${r.product_naam || r.sku || 'Product'}`);
    }
  }
  if (already.length && shipped.length === 0) {
    lines.push('Eerdere deelzending(en) staan gemarkeerd op de orderregels in Woo.');
  }
  return lines.join('\n');
}

function picklijstMatchKey(entity) {
  return entity?.product_id ?? entity?.sku ?? entity?.product_naam ?? 'unknown';
}

function picklijstItemIsFullyPicked(item) {
  if (!picklijstItemMoetNogGepicktWorden(item)) return true;
  const target = picklijstItemOpenPickAantal(item);
  if (item?.is_bundel && Array.isArray(item.onderdelen) && item.onderdelen.length > 0) {
    return item.onderdelen.every((c) => (c.scanned || 0) >= (c.aantal || 0));
  }
  return (item.scanned || 0) >= target;
}

function picklijstItemsReadyToShip(items) {
  return (items || []).filter((item) => {
    if (picklijstItemOpenPickAantal(item) <= 0) return false;
    if (item?.verzonden_volledig) return false;
    return picklijstItemIsFullyPicked(item);
  });
}

function picklijstHasOpenUnpickedItems(items) {
  return (items || []).some((item) => {
    if (picklijstItemOpenPickAantal(item) <= 0) return false;
    if (item?.verzonden_volledig) return false;
    return !picklijstItemIsFullyPicked(item);
  });
}

function buildShippedLineItemsFromPicklijst(order, picklijstItems) {
  const out = [];
  for (const regel of order?.regels || []) {
    const rest = orderRegelRestAantal(regel);
    if (rest <= 0) continue;
    const pickItem = (picklijstItems || []).find(
      (item) => picklijstMatchKey(item) === picklijstMatchKey(regel)
    );
    if (!pickItem || !picklijstItemIsFullyPicked(pickItem)) continue;
    const lineId = parseInt(regel.wc_line_item_id, 10);
    if (lineId > 0) {
      out.push({ line_item_id: lineId, quantity: rest });
    }
  }
  return out;
}

function buildShippedLineItemsForPickSession(order, picklijst) {
  if (!order) return [];
  const singleOrder = (picklijst?.sourceOrders || []).length === 1;
  if (singleOrder) {
    return buildShippedLineItemsFromPicklijst(order, picklijst?.items);
  }
  return buildShippedLineItemsForOrder(order);
}

function buildPartialShipmentNoteFromPickedPicklijst(order, picklijstItems) {
  const lines = ['Deelzending verzonden via Magazijn.'];
  const shipped = [];
  const pending = [];
  for (const regel of order?.regels || []) {
    const rest = orderRegelRestAantal(regel);
    if (rest <= 0) continue;
    const pickItem = (picklijstItems || []).find(
      (item) => picklijstMatchKey(item) === picklijstMatchKey(regel)
    );
    if (pickItem && picklijstItemIsFullyPicked(pickItem)) {
      shipped.push(regel);
    } else {
      pending.push(regel);
    }
  }
  if (shipped.length) {
    lines.push('Verzonden (gepickt):');
    for (const r of shipped) {
      lines.push(`- ${orderRegelRestAantal(r)}× ${r.product_naam || r.sku || 'Product'}`);
    }
  }
  if (pending.length) {
    lines.push('Nog open:');
    for (const r of pending) {
      lines.push(`- ${orderRegelRestAantal(r)}× ${r.product_naam || r.sku || 'Product'}`);
    }
  }
  return lines.join('\n');
}

function buildPartialShipmentNoteFromPicklijst(picklijst) {
  const items = picklijst?.items || [];
  const shipped = items.filter(picklijstItemIsFullyPicked);
  const pending = items.filter(
    (item) => picklijstItemOpenPickAantal(item) > 0 && !picklijstItemIsFullyPicked(item)
  );
  const lines = ['Deelzending verzonden via Magazijn.'];
  if (shipped.length) {
    lines.push('Verzonden (gepickt):');
    for (const it of shipped) {
      lines.push(`- ${picklijstItemOpenPickAantal(it) || it.aantal || 0}× ${it.product_naam || it.sku || 'Product'}`);
    }
  }
  if (pending.length) {
    lines.push('Nog open:');
    for (const it of pending) {
      lines.push(`- ${picklijstItemOpenPickAantal(it) || it.aantal || 0}× ${it.product_naam || it.sku || 'Product'}`);
    }
  }
  return lines.join('\n');
}

function picklijstItemVervoerAdviesLabels(item) {
  const vt = String(item?.verzendtype_leverbelofte || '').trim().toLowerCase();
  if (vt === 'standaard') return ['DPD'];
  if (vt === 'dhl') return ['DHL'];
  if (vt === 'pallet') return ['Pallet'];
  const legacy = String(item?.standaard_vervoerder || '').trim().toLowerCase();
  if (legacy === 'dpd') return ['DPD'];
  if (legacy === 'dhl') return ['DHL'];
  return [];
}

function picklijstVervoerderAdviesTekst(items) {
  const set = new Set();
  for (const it of items || []) {
    for (const lab of picklijstItemVervoerAdviesLabels(it)) set.add(lab);
  }
  const labels = ['DHL', 'DPD', 'Pallet'].filter((x) => set.has(x));
  if (labels.length === 0) return null;
  return `Advies: ${labels.join(', ')}`;
}

/**
 * Korte NL datum+tijd voor orderplaatsing, zodat magazijn op één oogopslag
 * kan zien of een order voor of na de cut-off tijd binnen is gekomen.
 * WooCommerce levert `date_created` standaard in store-tijd (zonder zone), wat voor
 * ons de juiste "werkelijke besteltijd" is. Valt terug op leeg als niet parsebaar.
 */
function formatOrderDateTime(iso) {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  const yy = String(d.getFullYear()).slice(-2);
  const hh = String(d.getHours()).padStart(2, '0');
  const mi = String(d.getMinutes()).padStart(2, '0');
  return `${dd}-${mm}-${yy} ${hh}:${mi}`;
}

function formatVerzendDatumNl(iso) {
  if (!iso) return '';
  const m = String(iso).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (!m) return String(iso);
  return `${m[3]}-${m[2]}-${m[1]}`;
}

function splitPicklijstOrders(orders) {
  const pickbaar = [];
  const uitgesteld = [];
  for (const o of orders || []) {
    if (o.pick_vrijgegeven === false) uitgesteld.push(o);
    else pickbaar.push(o);
  }
  uitgesteld.sort((a, b) => String(a.verzend_datum || '9999').localeCompare(String(b.verzend_datum || '9999')));
  return { pickbaar, uitgesteld };
}

/**
 * FIFO op order-niveau (zelfde logica als server sidebar-badge).
 * Virtuele voorraad = Woo-voorraad + totale vraag open orders.
 */
function computeNoStockOrderIds(orders) {
  const orderIdNum = (o) => (o.id && String(o.id).startsWith('wc-')) ? parseInt(o.id.replace('wc-', ''), 10) || 0 : 0;
  const sorted = [...(orders || [])].sort((a, b) => {
    const da = a.date_created ? new Date(a.date_created).getTime() : null;
    const db = b.date_created ? new Date(b.date_created).getTime() : null;
    if (da != null && db != null) return da - db;
    if (da != null) return -1;
    if (db != null) return 1;
    return orderIdNum(a) - orderIdNum(b);
  });
  const totalDemand = {};
  const currentStock = {};
  for (const order of sorted) {
    for (const r of order.regels || []) {
      const key = r.product_id ?? r.sku ?? r.product_naam ?? 'unknown';
      const need = r.aantal || 0;
      totalDemand[key] = (totalDemand[key] || 0) + need;
      if (!(key in currentStock)) {
        currentStock[key] = r.voorraad != null ? r.voorraad : Infinity;
      }
    }
  }
  const virtualStock = {};
  Object.keys(totalDemand).forEach((key) => {
    const stock = currentStock[key] != null ? currentStock[key] : Infinity;
    virtualStock[key] = stock === Infinity ? Infinity : (stock + (totalDemand[key] || 0));
  });
  const allocated = {};
  const noStockIds = new Set();
  for (const order of sorted) {
    let ok = true;
    for (const r of order.regels || []) {
      const key = r.product_id ?? r.sku ?? r.product_naam ?? 'unknown';
      const need = r.aantal || 0;
      const stock = virtualStock[key] != null ? virtualStock[key] : Infinity;
      const available = stock === Infinity ? Infinity : (stock - (allocated[key] || 0));
      if (available < need) {
        ok = false;
        break;
      }
    }
    if (ok) {
      for (const r of order.regels || []) {
        const key = r.product_id ?? r.sku ?? r.product_naam ?? 'unknown';
        allocated[key] = (allocated[key] || 0) + (r.aantal || 0);
      }
    } else {
      noStockIds.add(order.id);
    }
  }
  return noStockIds;
}

function computeMagazijnPickFifoStats(orders) {
  const all = orders || [];
  const noStockIds = computeNoStockOrderIds(all);
  const { pickbaar, uitgesteld } = splitPicklijstOrders(all);
  const afhandelbaarNu = pickbaar.filter((o) => !noStockIds.has(o.id));
  return {
    noStockIds,
    pickbaar,
    uitgesteld,
    afhandelbaarNu,
    afhandelbaarCount: afhandelbaarNu.length,
    backorderCount: noStockIds.size,
  };
}

/** Geen orders meer die nu pickbaar én afhandelbaar zijn (FIFO). */
function isMagazijnDagPickKlaar(orders) {
  return computeMagazijnPickFifoStats(orders).afhandelbaarCount === 0;
}

function buildMagazijnDagKlaarSubtekst(orders) {
  const { noStockIds, pickbaar, uitgesteld } = computeMagazijnPickFifoStats(orders);
  const backorders = pickbaar.filter((o) => noStockIds.has(o.id)).length;
  const parts = [];
  if (uitgesteld.length > 0) {
    parts.push(
      `${uitgesteld.length} order${uitgesteld.length === 1 ? '' : 's'} staan gepland voor later vandaag of morgen`
    );
  }
  if (backorders > 0) {
    parts.push(`${backorders} backorder${backorders === 1 ? '' : 's'} wacht${backorders === 1 ? '' : 'en'} op voorraad`);
  }
  return parts.length ? parts.join(' · ') : null;
}

const MAGAZIJN_DAG_KLAAR_MELDINGEN = [
  {
    title: 'Goed gewerkt, topper!',
    body: 'Alle orders die vandaag pickbaar waren zijn verwerkt. Tijd voor een bak koffie.',
    emoji: '☕',
  },
  {
    title: 'Alles verwerkt!',
    body: 'De picklijst voor vandaag is leeg. Geniet van je welverdiende koffie.',
    emoji: '🎉',
  },
  {
    title: 'Klaar voor vandaag!',
    body: 'Geen orders meer die nu pickbaar zijn. Sterk werk in het magazijn!',
    emoji: '💪',
  },
  {
    title: 'Top gedaan!',
    body: 'Alles wat vandaag kon worden verzonden is afgehandeld. Tijd voor een pauze.',
    emoji: '☕',
  },
];

function pickMagazijnDagKlaarMelding() {
  const idx = Math.floor(Math.random() * MAGAZIJN_DAG_KLAAR_MELDINGEN.length);
  return MAGAZIJN_DAG_KLAAR_MELDINGEN[idx];
}

function getMagazijnDagKlaarStorageKey() {
  const d = new Date();
  const dd = String(d.getDate()).padStart(2, '0');
  const mm = String(d.getMonth() + 1).padStart(2, '0');
  return `${d.getFullYear()}-${mm}-${dd}`;
}

function markMagazijnHadAfhandelbareOrders() {
  try {
    sessionStorage.setItem(`magazijn-had-afhandelbaar-${getMagazijnDagKlaarStorageKey()}`, '1');
  } catch (_) {
    /* noop */
  }
}

function hadMagazijnAfhandelbareOrdersToday() {
  try {
    return sessionStorage.getItem(`magazijn-had-afhandelbaar-${getMagazijnDagKlaarStorageKey()}`) === '1';
  } catch (_) {
    return false;
  }
}

function buildMagazijnDagKlaarMeldingPayload(orders, extra = {}) {
  return {
    ...pickMagazijnDagKlaarMelding(),
    subtekst: buildMagazijnDagKlaarSubtekst(orders),
    ...extra,
  };
}

/** Viering tonen: pickbaar werk af, en er was vandaag pick-werk of staan nog andere orders open. */
function isMagazijnDagPickKlaarForCelebration(orders) {
  if (!isMagazijnDagPickKlaar(orders)) return false;
  if (hadMagazijnAfhandelbareOrdersToday()) return true;
  const { pickbaar } = computeMagazijnPickFifoStats(orders);
  return pickbaar.length > 0;
}

function truncateOrderNote(text, max = 120) {
  const s = String(text || '').trim();
  if (!s) return '';
  return s.length <= max ? s : `${s.slice(0, max - 1)}…`;
}

function toWooIdForPicklijst(id) {
  if (id && String(id).startsWith('wc-')) return parseInt(String(id).replace('wc-', ''), 10);
  return parseInt(id, 10);
}

/**
 * Zelfde bundeling als bij het openen van een picklijst: regels over orders heen samenvoegen (incl. bundels).
 */
function aggregateOrdersToPickItems(orders) {
  const map = {};
  for (const order of orders || []) {
    for (const r of order.regels || []) {
      const key = r.product_id ?? r.sku ?? r.product_naam ?? 'unknown';
      if (!map[key]) {
        map[key] = {
          product_id: r.product_id,
          product_naam: r.product_naam,
          sku: r.sku,
          ean: r.ean,
          magazijn_locatie: r.magazijn_locatie || '',
          standaard_vervoerder: r.standaard_vervoerder || null,
          verzendtype_leverbelofte: r.verzendtype_leverbelofte || null,
          voorraad: r.voorraad != null ? r.voorraad : undefined,
          product_options: r.product_options,
          aantal: 0,
          scanned: 0,
          shipped_qty: 0,
          resterend: 0,
          verzonden_volledig: true,
          wc_line_item_id: r.wc_line_item_id || null,
          ordernummers: [],
          is_bundel: !!r.is_bundel,
          onderdelen: r.is_bundel && Array.isArray(r.onderdelen) ? [] : undefined,
        };
      }
      map[key].aantal += r.aantal || 0;
      const lineShipped = parseInt(r.shipped_qty, 10) || 0;
      const lineRest =
        r.resterend != null
          ? parseInt(r.resterend, 10) || 0
          : Math.max(0, (parseInt(r.aantal, 10) || 0) - lineShipped);
      map[key].shipped_qty = (map[key].shipped_qty || 0) + lineShipped;
      map[key].resterend = (map[key].resterend || 0) + lineRest;
      map[key].verzonden_volledig = map[key].verzonden_volledig && !!r.verzonden_volledig;
      if (!map[key].wc_line_item_id && r.wc_line_item_id) {
        map[key].wc_line_item_id = r.wc_line_item_id;
      }
      if (!map[key].standaard_vervoerder && r.standaard_vervoerder) {
        map[key].standaard_vervoerder = r.standaard_vervoerder;
      }
      if (!map[key].verzendtype_leverbelofte && r.verzendtype_leverbelofte) {
        map[key].verzendtype_leverbelofte = r.verzendtype_leverbelofte;
      }
      if (order.ordernummer && !map[key].ordernummers.includes(order.ordernummer)) {
        map[key].ordernummers.push(order.ordernummer);
      }
      if (r.product_options?.length && (!map[key].product_options || map[key].product_options.length === 0)) {
        map[key].product_options = r.product_options;
      }
      if (r.is_bundel && Array.isArray(r.onderdelen)) {
        map[key].is_bundel = true;
        if (!Array.isArray(map[key].onderdelen)) map[key].onderdelen = [];
        for (const child of r.onderdelen) {
          const childKey = child.product_id ?? child.sku ?? child.product_naam ?? 'unknown';
          let existing = map[key].onderdelen.find(
            (c) => (c.product_id ?? c.sku ?? c.product_naam ?? 'unknown') === childKey
          );
          if (!existing) {
            existing = {
              product_id: child.product_id,
              product_naam: child.product_naam,
              sku: child.sku,
              ean: child.ean,
              magazijn_locatie: child.magazijn_locatie || '',
              voorraad: child.voorraad != null ? child.voorraad : undefined,
              aantal_per_parent: child.aantal_per_parent || 1,
              aantal: 0,
              scanned: 0,
            };
            map[key].onderdelen.push(existing);
          }
          existing.aantal += child.aantal || 0;
        }
      }
    }
  }
  return Object.values(map);
}

function sortPicklijstItemsForMagazijn(items) {
  return [...(items || [])].sort((a, b) => {
    const hasStockA = picklijstItemHasPickbareVoorraad(a);
    const hasStockB = picklijstItemHasPickbareVoorraad(b);
    if (hasStockA !== hasStockB) return hasStockA ? -1 : 1;
    const locA = (a.magazijn_locatie || '').toLowerCase();
    const locB = (b.magazijn_locatie || '').toLowerCase();
    if (locA === locB) return (a.product_naam || '').localeCompare(b.product_naam || '');
    if (!locA) return 1;
    if (!locB) return -1;
    return locA.localeCompare(locB);
  });
}

function computePicklijstPayload(orders) {
  const selected = orders || [];
  const items = sortPicklijstItemsForMagazijn(aggregateOrdersToPickItems(selected));
  const ordernummers = selected.map((o) => o.ordernummer).filter(Boolean);
  const orderIds = selected.map((o) => o.id);
  const shopifyOrderIds = selected
    .filter((o) => o.is_shopify)
    .map((o) => toWooIdForPicklijst(o.id))
    .filter(Boolean);
  const orderVerzend = selected.map((o) => ({
    ordernummer: o.ordernummer,
    verzend_datum: o.verzend_datum || null,
    verzend_datum_nl: o.verzend_datum ? formatVerzendDatumNl(o.verzend_datum) : null,
    pick_vrijgegeven: o.pick_vrijgegeven !== false,
  }));
  return { items, ordernummers, orderIds, shopifyOrderIds, orderVerzend, sourceOrders: selected };
}

function picklijstSamenvattingItemKey(it) {
  return `${it.product_id ?? ''}-${it.sku ?? ''}-${(it.product_naam || '').slice(0, 48)}`;
}

const Magazijn = () => {
  const { searchTerm, setSearchTerm, setMagazijnSubTab } = useSearch();
  const [showScanner, setShowScanner] = useState(false);
  const [scanContext, setScanContext] = useState(null);
  const [showLocatieImport, setShowLocatieImport] = useState(false);
  const [locatieImportData, setLocatieImportData] = useState('');
  const [locatieImportMode, setLocatieImportMode] = useState('paste');
  const [locatieImportLoading, setLocatieImportLoading] = useState(false);
  const [activeTab, setActiveTab] = useState('pick'); // 'pick' | 'search' | 'retouren'
  const [retouren, setRetouren] = useState([]);
  const [retourenLoading, setRetourenLoading] = useState(false);
  const [retourenStatusFilter, setRetourenStatusFilter] = useState('');
  /** Aantal retouren dat nog magazijn-actie vraagt (zelfde filter als sidebar-werk). */
  const [retourenBadgeCount, setRetourenBadgeCount] = useState(null);
  const [showRetourModal, setShowRetourModal] = useState(false);
  const [retourLookupOrdernr, setRetourLookupOrdernr] = useState('');
  const [retourLookupEmail, setRetourLookupEmail] = useState('');
  const [retourLookupLoading, setRetourLookupLoading] = useState(false);
  const [retourLookupError, setRetourLookupError] = useState('');
  const [retourOrder, setRetourOrder] = useState(null);
  const [retourSelected, setRetourSelected] = useState({}); // { line_item_id: aantal }
  const [retourReason, setRetourReason] = useState('');
  const [retourCustomerNote, setRetourCustomerNote] = useState('');
  const [retourInternalNote, setRetourInternalNote] = useState('');
  const [retourSaving, setRetourSaving] = useState(false);
  const [retourDetail, setRetourDetail] = useState(null);
  const [retourDetailLoading, setRetourDetailLoading] = useState(false);
  const [retourTerugbetalenLaden, setRetourTerugbetalenLaden] = useState(false);
  const [retourOntvangstDetail, setRetourOntvangstDetail] = useState(null);
  const [retourOntvangstLaden, setRetourOntvangstLaden] = useState(false);
  const [ontvangstConditie, setOntvangstConditie] = useState('ok');
  const [ontvangstCode, setOntvangstCode] = useState('');
  const [ontvangstError, setOntvangstError] = useState('');
  const [ontvangstMsg, setOntvangstMsg] = useState('');
  const [ontvangstRegelLaden, setOntvangstRegelLaden] = useState(null);
  const ontvangstInputRef = useRef(null);
  const [ordersInProductie, setOrdersInProductie] = useState([]);
  const [ordersError, setOrdersError] = useState(null);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [picklijst, setPicklijst] = useState(null);
  const [showVerzendkeuze, setShowVerzendkeuze] = useState(false);
  const [verzendenLoading, setVerzendenLoading] = useState(false);
  /** Voorkomt dubbele process-order bij dubbele klik/tap vóór de eerste re-render (disabled is dan nog false). */
  const verzendenInFlightRef = useRef(false);
  const [verzendCarrier, setVerzendCarrier] = useState('dpd');
  const [verzendService, setVerzendService] = useState('DPD HOME');
  const [verzendLabels, setVerzendLabels] = useState('1');
  const [dagKlaarMelding, setDagKlaarMelding] = useState(null);
  const [dagKlaarBannerDismissed, setDagKlaarBannerDismissed] = useState(false);
  const [dagKlaarBannerMelding, setDagKlaarBannerMelding] = useState(null);
  const [picklijstEditLocatieKey, setPicklijstEditLocatieKey] = useState(null);
  const [picklijstEditLocatieValue, setPicklijstEditLocatieValue] = useState('');
  const [picklijstLocatieSaving, setPicklijstLocatieSaving] = useState(false);
  const [perOrderMode, setPerOrderMode] = useState(true);
  const [verzendDatumDraft, setVerzendDatumDraft] = useState({});
  const [verzendDatumOpen, setVerzendDatumOpen] = useState({});
  const [verzendDatumSaving, setVerzendDatumSaving] = useState(null);
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [selectedProductOpenVoorraad, setSelectedProductOpenVoorraad] = useState(null);
  const [selectedProductOpenVoorraadLoading, setSelectedProductOpenVoorraadLoading] = useState(false);
  const [locatieInput, setLocatieInput] = useState('');
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const searchInputRef = useRef(null);
  const locatieInputRef = useRef(null);
  const [printLabelReady, setPrintLabelReady] = useState(false);
  const labelSvgRef = useRef(null);
  const picklijstRef = useRef(null);
  picklijstRef.current = picklijst;
  const ordersInProductieRef = useRef([]);
  ordersInProductieRef.current = ordersInProductie;

  useEffect(() => {
    searchInputRef.current?.focus();
  }, []);

  useEffect(() => {
    if (!selectedProduct?.id) {
      setSelectedProductOpenVoorraad(null);
      setSelectedProductOpenVoorraadLoading(false);
      return undefined;
    }
    let cancelled = false;
    setSelectedProductOpenVoorraadLoading(true);
    setSelectedProductOpenVoorraad(null);
    api
      .get(`/producten/${selectedProduct.id}/open-order-voorraad`)
      .then((res) => {
        if (!cancelled) setSelectedProductOpenVoorraad(res.data || null);
      })
      .catch(() => {
        if (!cancelled) setSelectedProductOpenVoorraad(null);
      })
      .finally(() => {
        if (!cancelled) setSelectedProductOpenVoorraadLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [selectedProduct?.id]);

  const fetchOrders = async () => {
    setOrdersLoading(true);
    setOrdersError(null);
    try {
      const res = await api.get('/woocommerce/orders-in-behandeling');
      const data = res.data || [];
      setOrdersInProductie(data);
      return data;
    } catch (err) {
      setOrdersInProductie([]);
      setOrdersError(err.response?.data?.error || err.message || 'Kon orders niet ophalen');
      return [];
    } finally {
      setOrdersLoading(false);
    }
  };

  const refreshRetourenBadgeCount = useCallback(async () => {
    try {
      const res = await api.get('/retouren');
      const list = Array.isArray(res.data) ? res.data : [];
      setRetourenBadgeCount(list.filter(retourKanNogVerwerktWorden).length);
    } catch {
      setRetourenBadgeCount(0);
    }
  }, []);

  const fetchRetouren = useCallback(async (status) => {
    setRetourenLoading(true);
    try {
      const params = {};
      const eff = status !== undefined ? status : retourenStatusFilter;
      if (eff) params.status = eff;
      const res = await api.get('/retouren', { params });
      setRetouren(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setRetouren([]);
    } finally {
      setRetourenLoading(false);
      refreshRetourenBadgeCount();
    }
  }, [retourenStatusFilter, refreshRetourenBadgeCount]);

  /** Alleen bij openen Magazijn — niet opnieuw bij picklijst-state (anders dubbele WooCommerce-load). */
  useEffect(() => {
    fetchOrders();
    refreshRetourenBadgeCount();
  }, [refreshRetourenBadgeCount]);

  // Retouren laden zodra de tab geopend wordt of het filter wijzigt.
  useEffect(() => {
    if (activeTab !== 'retouren') return;
    fetchRetouren(retourenStatusFilter);
  }, [activeTab, retourenStatusFilter, fetchRetouren]);

  const resetRetourModal = () => {
    setRetourLookupOrdernr('');
    setRetourLookupEmail('');
    setRetourLookupError('');
    setRetourOrder(null);
    setRetourSelected({});
    setRetourReason('');
    setRetourCustomerNote('');
    setRetourInternalNote('');
  };

  const openNewRetourModal = () => {
    resetRetourModal();
    setShowRetourModal(true);
  };

  const closeNewRetourModal = () => {
    if (retourSaving) return;
    setShowRetourModal(false);
    resetRetourModal();
  };

  const lookupRetourOrder = async () => {
    const ordernr = retourLookupOrdernr.trim();
    if (!ordernr) {
      setRetourLookupError('Vul een ordernummer in');
      return;
    }
    setRetourLookupLoading(true);
    setRetourLookupError('');
    setRetourOrder(null);
    setRetourSelected({});
    try {
      const params = {};
      if (retourLookupEmail.trim()) params.email = retourLookupEmail.trim();
      const res = await api.get(`/retouren/lookup-order/${encodeURIComponent(ordernr)}`, { params });
      setRetourOrder(res.data);
      // start met alle aantallen op 0
      const initial = {};
      (res.data.regels || []).forEach((r) => {
        initial[r.wc_line_item_id] = 0;
      });
      setRetourSelected(initial);
    } catch (err) {
      setRetourLookupError(
        err.response?.data?.error || err.message || 'Kon order niet ophalen'
      );
    } finally {
      setRetourLookupLoading(false);
    }
  };

  const setRetourAantal = (lineItemId, value) => {
    const n = Math.max(0, parseInt(value || 0, 10) || 0);
    setRetourSelected((prev) => ({ ...prev, [lineItemId]: n }));
  };

  const retourTotaal = useMemo(() => {
    if (!retourOrder) return 0;
    return (retourOrder.regels || []).reduce((sum, r) => {
      const aantal = retourSelected[r.wc_line_item_id] || 0;
      return sum + aantal * (r.prijs_per_stuk || 0);
    }, 0);
  }, [retourOrder, retourSelected]);

  const submitRetour = async () => {
    if (!retourOrder) return;
    const regels = (retourOrder.regels || [])
      .map((r) => ({
        wc_line_item_id: r.wc_line_item_id,
        product_id: r.product_id,
        sku: r.sku,
        naam: r.naam,
        aantal_aangemeld: retourSelected[r.wc_line_item_id] || 0,
        prijs_per_stuk: r.prijs_per_stuk,
      }))
      .filter((r) => r.aantal_aangemeld > 0);
    if (regels.length === 0) {
      alert('Selecteer minimaal één regel met aantal > 0');
      return;
    }
    if (!retourReason) {
      alert('Kies een retourreden');
      return;
    }
    if (retourReason === RETOUR_REDEN_VALUE_ANDERS && !String(retourCustomerNote || '').trim()) {
      alert('Bij "Anders" is een toelichting verplicht');
      return;
    }
    const termijn = retourOrder.retour_termijn;
    if (termijn && !termijn.allowed) {
      const uiterste = termijn.uiterste_datum
        ? String(termijn.uiterste_datum).replace(/^(\d{4})-(\d{2})-(\d{2}).*/, '$3-$2-$1')
        : '—';
      const ok = window.confirm(
        `Let op: de klanttermijn is verstreken (meer dan 30 dagen na geschatte ontvangst).\n\nUiterste retourdatum was ${uiterste}.\n\nToch aanmelden als admin?`
      );
      if (!ok) return;
    }
    setRetourSaving(true);
    try {
      const payload = {
        ordernummer: retourOrder.order_nummer,
        // Intern: leeg laten = factuur-e-mail van de order; client-side mag alleen op ordernummer.
        klant_email: (retourLookupEmail || '').trim() || retourOrder.klant_email,
        aangemaakt_via: 'admin',
        regels,
        reden: retourReason,
        opmerkingen_klant: retourCustomerNote,
        opmerkingen_intern: retourInternalNote,
      };
      await api.post('/retouren', payload);
      if (termijn && !termijn.allowed) {
        alert('Retour aangemeld. Let op: klanttermijn was verstreken (30 dagen na geschatte ontvangst).');
      }
      setShowRetourModal(false);
      resetRetourModal();
      fetchRetouren(retourenStatusFilter);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Kon retour niet opslaan');
    } finally {
      setRetourSaving(false);
    }
  };

  const openRetourDetail = async (id) => {
    setRetourDetailLoading(true);
    try {
      const res = await api.get(`/retouren/${id}`, { params: { enrich: '1' } });
      setRetourDetail(res.data);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Kon retour niet laden');
    } finally {
      setRetourDetailLoading(false);
    }
  };

  const openRetourOntvangst = async (id) => {
    setRetourOntvangstLaden(true);
    setOntvangstError('');
    setOntvangstMsg('');
    setOntvangstCode('');
    try {
      const res = await api.get(`/retouren/${id}`, { params: { enrich: '1' } });
      setRetourOntvangstDetail(res.data);
      setOntvangstConditie('ok');
      setTimeout(() => ontvangstInputRef.current?.focus(), 200);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Kon retour niet laden');
    } finally {
      setRetourOntvangstLaden(false);
    }
  };

  const closeRetourOntvangst = () => {
    setRetourOntvangstDetail(null);
    setOntvangstError('');
    setOntvangstMsg('');
    setOntvangstCode('');
  };

  const doRetourVolledigeRegel = async (regel) => {
    if (!regel || !retourOntvangstDetail?.id) return false;
    const id = regel.id;
    const rest = retourToontvangstRest(regel);
    if (rest <= 0) return true;
    setOntvangstRegelLaden(id);
    setOntvangstError('');
    setOntvangstMsg('');
    try {
      const res = await api.post(`/retouren/${retourOntvangstDetail.id}/ontvangst/volledige-regel`, {
        regel_id: id,
        conditie: ontvangstConditie,
      });
      setRetourOntvangstDetail(res.data.retour);
      setOntvangstMsg(
        `Resterend (${rest}×) op "${(regel.naam || 'regel').slice(0, 50)}${(regel.naam || '').length > 50 ? '…' : ''}" in één keer geboekt.`
      );
      fetchRetouren(retourenStatusFilter);
      if (retourDetail?.id === res.data.retour.id) {
        setRetourDetail(res.data.retour);
      }
      ontvangstInputRef.current?.focus();
      return true;
    } catch (err) {
      setOntvangstError(err.response?.data?.error || err.message || 'Boeken mislukt');
      return false;
    } finally {
      setOntvangstRegelLaden(null);
    }
  };

  const doRetourOntvangstScan = async (raw) => {
    const code = String(raw || '').trim();
    if (!code || !retourOntvangstDetail?.id) return;
    if (['geweigerd', 'terugbetaald'].includes(retourOntvangstDetail.status) || retourOntvangstDetail.refund_status === 'succeeded') {
      setOntvangstError('Deze retour is gesloten voor ontvangst.');
      return;
    }
    setRetourOntvangstLaden(true);
    setOntvangstError('');
    setOntvangstMsg('');
    try {
      const res = await api.post(`/retouren/${retourOntvangstDetail.id}/ontvangst/scan`, {
        code,
        conditie: ontvangstConditie,
      });
      setRetourOntvangstDetail(res.data.retour);
      setOntvangstCode('');
      const p = res.data.product;
      if (p?.naam) {
        setOntvangstMsg(
          `${ontvangstConditie === 'ok' ? '✓' : '⚠'} ${p.naam} (${
            ontvangstConditie === 'ok' ? 'in orde; Woo-voorraad na terugbetaling' : 'defect'
          })` + (res.data.alles_ontvangen ? ' — alle regels binnen.' : '')
        );
      }
      setScanContext(null);
      setShowScanner(false);
      fetchRetouren(retourenStatusFilter);
      if (retourDetail?.id === res.data.retour.id) {
        setRetourDetail(res.data.retour);
      }
      ontvangstInputRef.current?.focus();
    } catch (err) {
      setOntvangstError(err.response?.data?.error || err.message || 'Scan mislukt');
    } finally {
      setRetourOntvangstLaden(false);
    }
  };

  const closeRetourDetail = () => setRetourDetail(null);

  const updateRetourStatus = async (id, status) => {
    try {
      await api.patch(`/retouren/${id}/status`, { status });
      // herlaad lijst en — als detail open is — ook detail
      fetchRetouren(retourenStatusFilter);
      if (retourDetail?.id === id) openRetourDetail(id);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Kon status niet bijwerken');
    }
  };

  const deleteRetour = async (id) => {
    if (!window.confirm('Weet je zeker dat je deze retour wilt verwijderen?')) return;
    try {
      await api.delete(`/retouren/${id}`);
      fetchRetouren(retourenStatusFilter);
      if (retourDetail?.id === id) setRetourDetail(null);
    } catch (err) {
      alert(err.response?.data?.error || err.message || 'Kon retour niet verwijderen');
    }
  };

  const formatRetourDatum = (s) => {
    if (!s) return '';
    const d = new Date(s);
    if (Number.isNaN(d.getTime())) return s;
    return d.toLocaleString('nl-NL', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const formatEuro = (n) => {
    const v = parseFloat(n);
    if (!Number.isFinite(v)) return '€ 0,00';
    return `€ ${v.toFixed(2).replace('.', ',')}`;
  };

  const retourStatusLabel = (s) =>
    ({
      aangemeld: 'Aangemeld',
      ontvangen: 'Ontvangen',
      terugbetaald: 'Terugbetaald',
      geweigerd: 'Geweigerd',
    }[s] || s);

  const retourRefundStatusLabel = (rs) =>
    ({
      pending: 'In afwachting',
      processing: 'Bezig',
      succeeded: 'Terugbetaald (gateway)',
      failed: 'Mislukt',
      bol_handmatig: 'Bol: handmatig (ticket)',
    }[rs] || rs);

  const retourToontvangstRest = (r) => {
    if (r == null) return 0;
    if (typeof r.rest_te_ontvangen === 'number') return r.rest_te_ontvangen;
    const a = parseInt(r.aantal_aangemeld, 10) || 0;
    const okc = parseInt(r.aantal_ok, 10) || 0;
    const dfc = parseInt(r.aantal_defect, 10) || 0;
    return Math.max(0, a - okc - dfc);
  };

  const retourAllRegelsInOntvangst = useMemo(() => isRetourOntvangstComplete(retourDetail), [retourDetail]);
  const retourKlaarVoorTerugbetaling = useMemo(
    () => isRetourKlaarVoorTerugbetaling(retourDetail),
    [retourDetail]
  );

  /** Client-side filter op retour-tab (zelfde term als navbar-zoekbalk). */
  const retourenGefilterd = useMemo(() => {
    const raw = searchTerm.trim();
    if (!raw) return retouren;
    const q = raw.toLowerCase();
    const qDigits = raw.replace(/\D/g, '');
    return retouren.filter((r) => {
      if (qDigits.length >= 4 && postcodeMatchesSearch(r.klant_postcode, raw)) {
        return true;
      }
      const chunks = [
        r.id != null ? String(r.id) : '',
        r.retournummer,
        r.ordernummer,
        r.wc_order_id != null ? String(r.wc_order_id) : '',
        r.klant_email,
        r.klant_naam,
        r.klant_postcode,
        r.bron,
        r.reden,
        r.status,
        retourStatusLabel(r.status),
      ].map((x) => String(x || '').toLowerCase());
      return chunks.some((c) => c.includes(q));
    });
  }, [retouren, searchTerm]);

  /** Ontvangst-modal: mag direct door naar detail + terugbetaal-knop (zelfde voorwaarden als daar). */
  const ontvangstNaarTerugbetaling = useMemo(() => {
    const ro = retourOntvangstDetail;
    if (!ro) return { mag: false, title: '', retourId: null };
    const klaarVoorTerugbetaling = isRetourKlaarVoorTerugbetaling(ro);
    const mag =
      ['aangemeld', 'ontvangen'].includes(ro.status) &&
      ro.refund_status !== 'succeeded' &&
      ro.refund_status !== 'bol_handmatig' &&
      klaarVoorTerugbetaling;
    let title = 'Open het retourdetail; daar staat Goedkeuren & terugbetalen.';
    if (!klaarVoorTerugbetaling) {
      title =
        'Registreer eerst minimaal één ontvangen stuk (OK of defect). Bij een gedeeltelijke retour hoeven niet alle orderregels ontvangen te zijn.';
    } else if (ro.refund_status === 'succeeded' || ro.refund_status === 'bol_handmatig') {
      title = 'Deze retour is al afgerekend.';
    } else if (ro.status !== 'ontvangen') {
      title = 'Zet de retour op status ontvangen (gebeurt meestal automatisch na scannen).';
    }
    return { mag, title, retourId: ro.id };
  }, [retourOntvangstDetail]);

  const terugbetalenRetour = async (id) => {
    if (retourTerugbetalenLaden || !id) return;
    if (!retourKlaarVoorTerugbetaling) {
      window.alert(
        'Registreer eerst minimaal één ontvangen stuk in ontvangst (OK of defect). Bij een gedeeltelijke retour hoeven niet alle producten van de bestelling ontvangen te zijn.'
      );
      return;
    }
    if (
      !window.confirm(
        `Bevestig afhandeling voor ${formatEuro(retourDetail?.refund_bedrag)}:\n\n` +
          `• MultiSafePay-order: terugbetaling via WooCommerce + restock.\n` +
          `• Bol.com-marktplaatsorder (met Bol-referentie in de order): géén automatische uitbetaling — er wordt een ticket aangemaakt om handmatig via Bol terug te betalen.\n\n` +
          `Doorgaan?`
      )
    ) {
      return;
    }
    setRetourTerugbetalenLaden(true);
    try {
      const res = await api.post(`/retouren/${id}/terugbetalen`);
      const r = res.data?.retour;
      if (res.data?.bol_handmatig) {
        window.alert(
          `Bol-retour: in WooCommerce is een refund aangemaakt met restock (zonder betalingsgateway). ` +
            `Is het retourbedrag gelijk aan het volledige orderbedrag, dan staat de Woo-order nu op Terugbetaald/refunded. ` +
            `Ticket #${res.data.ticket_id}: betaal het bedrag handmatig terug aan de klant via Bol (referentie ${res.data.bol_order_ref}).`
        );
      }
      if (r) setRetourDetail(r);
      await fetchRetouren(retourenStatusFilter);
      if (retourOntvangstDetail?.id === id && r) {
        setRetourOntvangstDetail(r);
      }
    } catch (err) {
      window.alert(err.response?.data?.error || err.message || 'Terugbetalen mislukt');
    } finally {
      setRetourTerugbetalenLaden(false);
    }
  };

  useEffect(() => {
    setMagazijnSubTab(activeTab);
  }, [activeTab, setMagazijnSubTab]);

  useEffect(() => {
    return () => setMagazijnSubTab(null);
  }, [setMagazijnSubTab]);

  useEffect(() => {
    if (activeTab !== 'search') return undefined;
    if (!searchTerm || searchTerm.trim().length < 2) {
      setResults([]);
      return undefined;
    }
    let cancelled = false;
    const timer = setTimeout(async () => {
      setLoading(true);
      try {
        const res = await api.get('/producten/magazijn/zoek', {
          params: { q: searchTerm.trim() }
        });
        if (!cancelled) setResults(res.data || []);
      } catch (err) {
        if (!cancelled) setResults([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    }, 200);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [searchTerm, activeTab]);

  const selectProduct = (p) => {
    setSelectedProduct(p);
    setLocatieInput(p.magazijn_locatie || '');
    setSaved(false);
    setTimeout(() => locatieInputRef.current?.focus(), 100);
  };

  const clearSelection = () => {
    setPrintLabelReady(false);
    setSelectedProduct(null);
    setSelectedProductOpenVoorraad(null);
    setLocatieInput('');
    setSaved(false);
    searchInputRef.current?.focus();
  };

  useEffect(() => {
    if (!printLabelReady || !selectedProduct || !labelSvgRef.current) return undefined;
    const spec = getBarcodeSpec(selectedProduct);
    if (!spec) {
      setPrintLabelReady(false);
      return undefined;
    }
    const ok = renderBarcodeOnSvg(labelSvgRef.current, spec);
    if (!ok) {
      setPrintLabelReady(false);
      window.alert('Label kon niet worden voorbereid (streepjescode). Probeer opnieuw of open het product op de productpagina.');
      return undefined;
    }

    const afterPrint = () => {
      setPrintLabelReady(false);
      window.removeEventListener('afterprint', afterPrint);
    };
    window.addEventListener('afterprint', afterPrint);
    const timer = window.setTimeout(() => window.print(), 120);

    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('afterprint', afterPrint);
    };
  }, [printLabelReady, selectedProduct]);

  const handlePrintMagazijnProductLabel = () => {
    if (!getBarcodeSpec(selectedProduct)) {
      alert('Geen EAN of SKU om te coderen op dit product.');
      return;
    }
    setPrintLabelReady(true);
  };

  const saveLocatie = async () => {
    if (!selectedProduct) return;
    setSaving(true);
    setSaved(false);
    try {
      await api.patch(`/producten/${selectedProduct.id}/locatie`, {
        magazijn_locatie: locatieInput.trim() || null
      });
      setSelectedProduct(prev => ({ ...prev, magazijn_locatie: locatieInput.trim() || null }));
      setSaved(true);
      setResults(prev => prev.map(p =>
        p.id === selectedProduct.id
          ? { ...p, magazijn_locatie: locatieInput.trim() || null }
          : p
      ));
    } catch (err) {
      alert('Opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setSaving(false);
    }
  };

  const parseLocatieData = (text) => {
    const lines = text.trim().split('\n').filter((l) => l.trim());
    const data = [];
    const norm = (s) => String(s || '').trim().replace(/\D/g, '');
    for (const line of lines) {
      const parts = line.split(/[\t,;]|\s{2,}/).map((p) => p.trim()).filter(Boolean);
      const ean = parts[0] ? (norm(parts[0]) || parts[0]) : null;
      const locatie = parts[1] != null ? String(parts[1]).trim() : null;
      if (ean && locatie !== null) data.push({ ean, magazijn_locatie: locatie });
    }
    return data;
  };

  const handleLocatieImport = async (dataToImport) => {
    if (!dataToImport?.length) {
      alert('Geen geldige data. Format: EAN en locatie per regel (tab, komma of puntkomma als scheidingsteken).');
      return;
    }
    setLocatieImportLoading(true);
    try {
      const res = await api.post('/producten/bulk-update-locatie', { locatieData: dataToImport });
      const n = res.data?.success?.length || 0;
      const notFound = res.data?.notFound?.length || 0;
      alert(`${n} locatie(s) geïmporteerd.${notFound > 0 ? ` ${notFound} niet gevonden.` : ''}`);
      setShowLocatieImport(false);
      setLocatieImportData('');
      if (searchTerm) {
        const r = await api.get('/producten/magazijn/zoek', { params: { q: searchTerm } });
        setResults(r.data || []);
      }
    } catch (err) {
      alert('Import mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setLocatieImportLoading(false);
    }
  };

  const handleLocatiePasteImport = () => {
    handleLocatieImport(parseLocatieData(locatieImportData));
  };

  const handleLocatieFileUpload = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result);
        const wb = XLSX.read(data, { type: 'array' });
        const firstSheet = wb.Sheets[wb.SheetNames[0]];
        const rows = XLSX.utils.sheet_to_json(firstSheet, { header: 1 });
        const locatieData = [];
        const eanKeywords = ['ean', 'ean13', 'barcode', 'gtin', 'artikelnummer'];
        const locKeywords = ['locatie', 'magazijn', 'opslag', 'plek'];
        let eanCol = 0;
        let locCol = 1;
        if (rows.length > 0) {
          const headers = (rows[0] || []).map((h) => String(h || '').toLowerCase());
          const ei = headers.findIndex((h) => eanKeywords.some((k) => h.includes(k)));
          const li = headers.findIndex((h) => locKeywords.some((k) => h.includes(k)));
          if (ei >= 0) eanCol = ei;
          if (li >= 0) locCol = li;
        }
        for (let i = 1; i < rows.length; i++) {
          const row = Array.isArray(rows[i]) ? rows[i] : Object.values(rows[i] || {});
          const eanVal = row[eanCol];
          const locVal = row[locCol];
          const ean = eanVal ? String(eanVal).replace(/\D/g, '').trim() || String(eanVal).trim() : null;
          if (ean && locVal != null && String(locVal).trim()) {
            locatieData.push({ ean, magazijn_locatie: String(locVal).trim() });
          }
        }
        handleLocatieImport(locatieData);
      } catch (err) {
        alert('Excel kon niet worden gelezen: ' + err.message);
      }
    };
    reader.readAsArrayBuffer(file);
    e.target.value = '';
  };

  const applyVerzendDatumToOrder = useCallback((orderId, payload) => {
    setOrdersInProductie((prev) =>
      prev.map((o) =>
        o.id === orderId
          ? {
              ...o,
              verzend_datum: payload.verzend_datum ?? null,
              verzend_notitie: payload.verzend_notitie ?? o.verzend_notitie,
              pick_vrijgegeven: payload.pick_vrijgegeven !== false,
              pick_status: payload.pick_status ?? o.pick_status,
              pick_uitgesteld_reden: payload.pick_uitgesteld_reden ?? null,
              pick_vrijgave_datum: payload.pick_vrijgave_datum ?? o.pick_vrijgave_datum,
              pick_vrijgave_uur: payload.pick_vrijgave_uur ?? o.pick_vrijgave_uur,
            }
          : o
      )
    );
  }, []);

  const saveOrderVerzendDatum = async (order, dateValue) => {
    const wooId = toWooIdForPicklijst(order.id);
    if (!wooId) return;
    setVerzendDatumSaving(order.id);
    try {
      const res = await api.put(`/woocommerce/orders/${wooId}/verzend-datum`, {
        verzend_datum: dateValue || null,
        notitie: order.verzend_notitie || null,
      });
      applyVerzendDatumToOrder(order.id, res.data || {});
      setVerzendDatumDraft((prev) => {
        const next = { ...prev };
        delete next[order.id];
        return next;
      });
    } catch (err) {
      alert('Verzenddatum opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setVerzendDatumSaving(null);
    }
  };

  const maakPicklijst = (singleOrder) => {
    if (!singleOrder) return;
    if (singleOrder.pick_vrijgegeven === false) {
      alert(singleOrder.pick_uitgesteld_reden || 'Deze order is nog niet vrijgegeven voor picking.');
      return;
    }
    setPicklijstEditLocatieKey(null);
    setPicklijst(computePicklijstPayload([singleOrder]));
  };

  const ordersPickbaarNu = useMemo(
    () => (ordersInProductie || []).filter((o) => o.pick_vrijgegeven !== false),
    [ordersInProductie]
  );

  const picklijstOrderSplit = useMemo(() => splitPicklijstOrders(ordersInProductie), [ordersInProductie]);

  const orderFifoStats = useMemo(() => computeMagazijnPickFifoStats(ordersInProductie), [ordersInProductie]);

  useEffect(() => {
    if (orderFifoStats.afhandelbaarCount > 0) {
      markMagazijnHadAfhandelbareOrders();
    }
  }, [orderFifoStats.afhandelbaarCount]);

  const showDagKlaarBanner =
    !picklijst &&
    !ordersLoading &&
    !ordersError &&
    isMagazijnDagPickKlaarForCelebration(ordersInProductie) &&
    !dagKlaarBannerDismissed;

  useEffect(() => {
    if (!showDagKlaarBanner) return;
    setDagKlaarBannerMelding((prev) =>
      prev || buildMagazijnDagKlaarMeldingPayload(ordersInProductie)
    );
  }, [showDagKlaarBanner, ordersInProductie]);

  const dismissDagKlaarBanner = () => {
    setDagKlaarBannerDismissed(true);
    setDagKlaarBannerMelding(null);
  };

  const maakPicklijstVanAlleOrders = () => {
    if (!ordersPickbaarNu.length) {
      alert('Geen orders die nu pickbaar zijn (controleer uitgestelde orders onderaan).');
      return;
    }
    setPicklijstEditLocatieKey(null);
    setPicklijst(computePicklijstPayload(ordersPickbaarNu));
  };

  const picklijstPayloadAlleOrders = useMemo(
    () => computePicklijstPayload(ordersPickbaarNu),
    [ordersPickbaarNu]
  );

  const allesGepickt = () => {
    setPicklijst(null);
    setShowVerzendkeuze(false);
    setVerzendLabels('1');
    setPicklijstEditLocatieKey(null);
  };

  const codeMatchesEntry = (code, entry) => {
    const norm = String(code).trim().replace(/\D/g, '') || String(code).trim();
    const normLower = String(code).trim().toLowerCase();
    const itemEan = String(entry.ean || '').replace(/\D/g, '');
    const itemSku = String(entry.sku || '').toLowerCase();
    if (itemEan && (itemEan === norm || itemEan.slice(-norm.length) === norm || norm.slice(-itemEan.length) === itemEan)) return true;
    if (itemSku && (itemSku === normLower || itemSku.includes(normLower) || normLower.includes(itemSku))) return true;
    if (itemEan === norm) return true;
    return false;
  };

  /**
   * Geef de match op een picklijst-regel terug. Bij bundels wordt eerst gezocht
   * in de onderliggende onderdelen (die zijn immers de echte fysieke producten);
   * pas als het hoofdproduct zelf als gewoon product matcht, telt het mee.
   */
  const matchProductByCode = (code, items) => {
    if (!code || !items?.length) return null;
    for (const item of items) {
      if (!picklijstItemMoetNogGepicktWorden(item)) continue;
      if (item.is_bundel && Array.isArray(item.onderdelen)) {
        for (const child of item.onderdelen) {
          if (codeMatchesEntry(code, child)) {
            return { kind: 'bundel-child', item, child };
          }
        }
      }
      if (codeMatchesEntry(code, item)) {
        return { kind: 'item', item };
      }
    }
    return null;
  };

  const itemKey = (i) => `${i.product_id ?? ''}-${i.sku ?? ''}-${i.product_naam ?? ''}`;
  const childKey = (c) => `${c.product_id ?? ''}-${c.sku ?? ''}-${c.product_naam ?? ''}`;

  const startEditPicklijstLocatie = (item) => {
    setPicklijstEditLocatieKey(itemKey(item));
    setPicklijstEditLocatieValue(item.magazijn_locatie || '');
  };

  const cancelEditPicklijstLocatie = () => {
    setPicklijstEditLocatieKey(null);
    setPicklijstEditLocatieValue('');
  };

  const savePicklijstLocatie = async (item, newValue) => {
    const key = itemKey(item);
    const productId = item.product_id;
    if (productId == null) return;
    setPicklijstLocatieSaving(true);
    try {
      await api.patch(`/producten/${productId}/locatie`, {
        magazijn_locatie: newValue != null ? String(newValue).trim() || null : null,
      });
      const value = newValue != null ? String(newValue).trim() || '' : '';
      setPicklijst((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((i) => (itemKey(i) === key ? { ...i, magazijn_locatie: value } : i)),
        };
      });
      setPicklijstEditLocatieKey(null);
      setPicklijstEditLocatieValue('');
    } catch (err) {
      alert('Locatie opslaan mislukt: ' + (err.response?.data?.error || err.message));
    } finally {
      setPicklijstLocatieSaving(false);
    }
  };

  const handlePicklijstScan = (code) => {
    if (!picklijst?.items) return;
    const match = matchProductByCode(code, picklijst.items);
    if (!match) {
      setSearchTerm(code);
      return;
    }
    if (match.kind === 'bundel-child') {
      const parentKey = itemKey(match.item);
      const targetChildKey = childKey(match.child);
      setPicklijst((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((i) => {
            if (itemKey(i) !== parentKey) return i;
            const onderdelen = (i.onderdelen || []).map((c) => {
              if (childKey(c) !== targetChildKey) return c;
              return { ...c, scanned: Math.min(c.aantal, (c.scanned || 0) + 1) };
            });
            return { ...i, onderdelen };
          }),
        };
      });
    } else {
      const key = itemKey(match.item);
      setPicklijst((prev) => {
        if (!prev) return prev;
        return {
          ...prev,
          items: prev.items.map((i) => {
            if (itemKey(i) !== key) return i;
            return { ...i, scanned: Math.min(picklijstItemOpenPickAantal(i), (i.scanned || 0) + 1) };
          }),
        };
      });
    }
    setShowScanner(false);
    setScanContext(null);
  };

  const handleManualPicked = (item) => {
    if (!picklijstItemMoetNogGepicktWorden(item)) return;
    const key = itemKey(item);
    const scanned = item.scanned || 0;
    const aantal = picklijstItemOpenPickAantal(item);
    if (aantal <= 0 || scanned >= aantal) return;
    const msg = '⚠️ HANDMATIG ALS GEPIKT MARKEREN\n\n' +
      'De scanner werkt niet bij dit product.\n\n' +
      `Product: ${item.product_naam || 'Onbekend'}\n` +
      (item.ean ? `EAN: ${item.ean}\n` : '') +
      `Aantal: ${aantal}\n\n` +
      'Weet je ZEKER dat je dit product handmatig als volledig gepikt wilt markeren?\n\n' +
      'Gebruik dit alleen als de scanner niet werkt. Dit kan niet ongedaan worden.';
    if (!window.confirm(msg)) return;
    setPicklijst((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((i) => {
          if (itemKey(i) !== key) return i;
          return { ...i, scanned: picklijstItemOpenPickAantal(i) || 0 };
        }),
      };
    });
  };

  /** Markeer één onderdeel uit een bundel handmatig als gepikt. */
  const handleManualPickedChild = (parent, child) => {
    const parentKey = itemKey(parent);
    const targetKey = childKey(child);
    const scanned = child.scanned || 0;
    const aantal = child.aantal || 0;
    if (scanned >= aantal) return;
    const msg = '⚠️ HANDMATIG ALS GEPIKT MARKEREN\n\n' +
      `Onderdeel: ${child.product_naam || 'Onbekend'}\n` +
      (child.ean ? `EAN: ${child.ean}\n` : '') +
      `Aantal: ${aantal}\n\n` +
      'Bevestig om dit onderdeel handmatig als gepikt te markeren.';
    if (!window.confirm(msg)) return;
    setPicklijst((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: prev.items.map((i) => {
          if (itemKey(i) !== parentKey) return i;
          const onderdelen = (i.onderdelen || []).map((c) => {
            if (childKey(c) !== targetKey) return c;
            return { ...c, scanned: c.aantal };
          });
          return { ...i, onderdelen };
        }),
      };
    });
  };

  /** Een bundel-regel is "gepickt" als alle onderdelen volledig gescand zijn; voor
   *  een gewone regel: scanned ≥ resterend. */
  const itemFullyPicked = picklijstItemIsFullyPicked;

  const isSingleOrderPicklijst = (picklijst?.sourceOrders || []).length === 1;
  const picklijstReadyToShip = useMemo(
    () => picklijstItemsReadyToShip(picklijst?.items),
    [picklijst]
  );
  const picklijstHasDeelzending = useMemo(() => {
    const sources = picklijst?.sourceOrders || [];
    if (sources.length === 1) {
      const pickedSubset =
        picklijstReadyToShip.length > 0 && picklijstHasOpenUnpickedItems(picklijst?.items);
      return pickedSubset || orderHasDeelzending(sources[0]);
    }
    return picklijstCanPartialShip(picklijst?.items);
  }, [picklijst, picklijstReadyToShip]);
  const picklijstRequiredBeforeShip = useMemo(
    () => picklijstItemsRequiredBeforeShip(picklijst?.items),
    [picklijst]
  );
  const canVerzendenPicklijst = isSingleOrderPicklijst
    ? picklijstReadyToShip.length > 0
    : picklijstRequiredBeforeShip.length > 0 &&
      picklijstRequiredBeforeShip.every(picklijstItemIsFullyPicked);

  const picklijstVervoerderAdvies = useMemo(
    () => picklijstVervoerderAdviesTekst(picklijst?.items),
    [picklijst]
  );

  /** Zodra een picklijst opent: vul aan vanuit lokale productkaart; overschrijf geen waarden uit WooCommerce (die kunnen in DB leeg zijn). */
  const picklijstEnrichKey = useMemo(() => {
    if (!picklijst?.items?.length) return null;
    const pids = [...new Set(picklijst.items.map((i) => i.product_id).filter(Boolean))].sort(
      (a, b) => Number(a) - Number(b)
    );
    return `${(picklijst.ordernummers || []).join('|')}#${pids.join(',')}`;
  }, [picklijst]);

  useEffect(() => {
    if (!picklijstEnrichKey) return;
    const ids = [...new Set((picklijstRef.current?.items || []).map((i) => i.product_id).filter(Boolean))];
    if (ids.length === 0) return;

    let cancelled = false;
    (async () => {
      const map = {};
      try {
        const res = await api.get('/producten/magazijn/verzendvelden', {
          params: { ids: ids.join(',') }
        });
        const raw = res.data && typeof res.data === 'object' ? res.data : {};
        for (const id of ids) {
          const row = raw[id] ?? raw[String(id)];
          map[id] = row
            ? {
                sku: row.sku ?? null,
                ean: row.ean ?? null,
                standaard_vervoerder: row.standaard_vervoerder ?? null,
                verzendtype_leverbelofte: row.verzendtype_leverbelofte ?? null
              }
            : { sku: null, ean: null, standaard_vervoerder: null, verzendtype_leverbelofte: null };
        }
      } catch {
        for (const id of ids) {
          map[id] = { sku: null, ean: null, standaard_vervoerder: null, verzendtype_leverbelofte: null };
        }
      }
      if (cancelled) return;
      setPicklijst((prev) => {
        if (!prev) return prev;
        const nextItems = prev.items.map((it) => {
          if (!it.product_id) return it;
          const row = map[it.product_id];
          if (row === undefined) return it;
          const apiVtRaw = row.verzendtype_leverbelofte;
          const apiSvRaw = row.standaard_vervoerder;
          const apiVt =
            apiVtRaw != null && String(apiVtRaw).trim() !== ''
              ? String(apiVtRaw).trim().toLowerCase()
              : null;
          const apiSv =
            apiSvRaw != null && String(apiSvRaw).trim() !== ''
              ? String(apiSvRaw).trim().toLowerCase()
              : null;

          let verzendtype_leverbelofte = it.verzendtype_leverbelofte || null;
          let standaard_vervoerder = it.standaard_vervoerder || null;

          if (apiVt && ['standaard', 'dhl', 'pallet'].includes(apiVt)) {
            verzendtype_leverbelofte = apiVt;
            if (apiVt === 'standaard') standaard_vervoerder = 'dpd';
            else if (apiVt === 'dhl') standaard_vervoerder = 'dhl';
            else if (apiVt === 'pallet') standaard_vervoerder = null;
          } else if (apiSv === 'dpd' || apiSv === 'dhl') {
            if (!verzendtype_leverbelofte) standaard_vervoerder = apiSv;
          }

          const nextEan = it.ean || row.ean || null;
          const nextSku = it.sku || row.sku || null;
          if (
            (it.standaard_vervoerder || null) === (standaard_vervoerder || null) &&
            (it.verzendtype_leverbelofte || null) === (verzendtype_leverbelofte || null) &&
            (it.ean || null) === (nextEan || null) &&
            (it.sku || null) === (nextSku || null)
          ) {
            return it;
          }
          return { ...it, standaard_vervoerder, verzendtype_leverbelofte, ean: nextEan, sku: nextSku };
        });
        if (nextItems.every((it, i) => it === prev.items[i])) return prev;
        return { ...prev, items: nextItems };
      });
    })();

    return () => {
      cancelled = true;
    };
  }, [picklijstEnrichKey]);

  const picklijstGeenVervoerderUitleg = useMemo(() => {
    if (!picklijst?.items?.length) return '';
    const lines = picklijst.items;
    if (lines.some((i) => !i.product_id)) {
      return 'Order-SKU/EAN komt niet overeen met een product in voorraad';
    }
    return 'Nog geen verzendtype op product (bewerken → Verzendtype)';
  }, [picklijst]);

  const humanizeProcessOrderError = (rawError) => {
    let text = '';
    if (typeof rawError === 'string') text = rawError;
    else if (rawError == null) text = '';
    else {
      try {
        text = JSON.stringify(rawError);
      } catch {
        text = String(rawError);
      }
    }

    const msg = (text || '').trim();
    const lower = msg.toLowerCase();

    const nameTooLongPatterns = [
      /name.{0,30}too.{0,10}long/i,
      /receiver.{0,30}too.{0,10}long/i,
      /consignee.{0,30}too.{0,10}long/i,
      /customer.{0,30}too.{0,10}long/i,
      /company.{0,30}too.{0,10}long/i,
      /naam.{0,30}te.{0,10}lang/i,
      /dpd.*(name|naam).*(long|lang)/i,
    ];

    if (nameTooLongPatterns.some((re) => re.test(msg))) {
      return 'Klantnaam is te lang voor DPD. Kort (bedrijfs)naam in WooCommerce in en probeer opnieuw.';
    }

    if (!msg || lower === 'ok') {
      return 'Order kon niet verwerkt worden. Controleer in WordPress (Order Afhandeling/DPD) waarom het label niet gemaakt kon worden.';
    }

    return msg;
  };

  const formatProcessOrderError = (orderId, rawError) => `${orderId}: ${humanizeProcessOrderError(rawError)}`;

  /** PDF in browser (fallback als PrintNode niet geconfigureerd is of faalt). */
  /** PDF in browser (fallback als PrintNode niet geconfigureerd is of faalt). */
  const openShopifyPackingSlipInBrowser = async (wooOrderId) => {
    /**
     * Sync een leeg tabblad openen (anders pop-up block na fetch).
     * Geen noopener: anders kun je in sommige browsers het document niet vullen;
     * alleen `location.replace(blob:…)` geeft op Safari/Edge vaak een permanent wit scherm.
     */
    let popup = null;
    try {
      popup = window.open('about:blank', '_blank');
    } catch (_) {
      /* … */
    }

    const closePopup = () => {
      try {
        if (popup && !popup.closed) popup.close();
      } catch (_) {
        /* … */
      }
    };

    const alertBlobError = async (blob) => {
      let message = 'Pakbon kon niet worden geladen.';
      if (blob instanceof Blob) {
        try {
          const t = await blob.text();
          const j = JSON.parse(t);
          if (j.error) message = j.error;
        } catch (_) {
          /* … */
        }
      }
      alert(message);
    };

    try {
      const res = await api.get(`/shopify-orders/packing-slip/${wooOrderId}`, { responseType: 'blob' });
      const blob = res.data;
      if (!blob || blob.size === 0) {
        closePopup();
        alert('Geen pakbon ontvangen (lege response).');
        return;
      }

      if (blob.type && blob.type.includes('json')) {
        closePopup();
        await alertBlobError(blob);
        return;
      }

      const pdfBlob =
        blob.type && blob.type.includes('pdf') ? blob : new Blob([blob], { type: 'application/pdf' });
      const url = URL.createObjectURL(pdfBlob);

      /**
       * PDF in pop-up met eigen balk: Sluiten + Afdrukken. Geen auto-print (zeker op mobiel
       * blokkeert dat terugkeren naar de app). window.close() werkt niet overal → focus opener + hint.
       */
      const writePdfHtml = (win, src) => {
        if (!win || win.closed) return false;
        const esc = String(src).replace(/&/g, '&amp;').replace(/"/g, '&quot;');
        try {
          win.document.open();
          win.document.write(
            '<!DOCTYPE html><html><head><meta charset="utf-8">' +
              '<meta name="viewport" content="width=device-width,initial-scale=1">' +
              '<title>Pakbon</title>' +
              '<style>*{box-sizing:border-box}html,body{height:100%;margin:0;background:#3d3d3d}' +
              '.bar{position:fixed;top:0;left:0;right:0;z-index:10000;display:flex;flex-wrap:wrap;align-items:center;justify-content:space-between;gap:8px;padding:10px 12px;background:#0d9488;color:#fff;font:600 15px system-ui,-apple-system,BlinkMacSystemFont,sans-serif;box-shadow:0 2px 10px rgba(0,0,0,.25)}' +
              '.bar span{flex:1;min-width:0}' +
              '.bar .actions{display:flex;gap:8px;flex-shrink:0}' +
              '.bar button{border:none;background:#fff;color:#0d9488;padding:10px 14px;border-radius:8px;font:inherit;font-weight:700;cursor:pointer;-webkit-tap-highlight-color:transparent}' +
              '.bar button:active{opacity:.92}' +
              'embed{position:fixed;top:52px;left:0;right:0;bottom:0;width:100%;border:0;background:#525659}</style></head><body>' +
              '<div class="bar"><span>Pakbon</span><div class="actions">' +
              '<button type="button" id="printPakbon">Afdrukken</button>' +
              '<button type="button" id="closePakbon">Sluiten</button></div></div>' +
              '<embed src="' +
              esc +
              '" type="application/pdf" title="Pakbon" />' +
              '<script>(function(){function backToApp(){try{if(window.opener&&!window.opener.closed){window.opener.focus();}}catch(e){}}' +
              'document.getElementById("closePakbon").onclick=function(){backToApp();try{window.close();}catch(e){};' +
              'setTimeout(function(){if(window.closed)return;alert("Sluit dit tabblad via het kruisje of het tabbladen-menu van de browser om terug te gaan naar het programma.");},250);};' +
              'document.getElementById("printPakbon").onclick=function(){try{window.print();}catch(e){alert("Gebruik het deelmenu van de browser om te printen.");}};})();<' +
              '/script></body></html>'
          );
          win.document.close();
          return true;
        } catch (_) {
          return false;
        }
      };

      if (popup && !popup.closed) {
        const filled = writePdfHtml(popup, url);
        if (!filled) {
          try {
            popup.location.replace(url);
          } catch (_) {
            /* … */
          }
        }
        try {
          popup.focus();
        } catch (_) {
          /* … */
        }
      } else {
        const a = document.createElement('a');
        a.href = url;
        a.target = '_blank';
        a.rel = 'noopener noreferrer';
        a.download = `pakbon-${wooOrderId}.pdf`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      }
      setTimeout(() => URL.revokeObjectURL(url), 120000);
    } catch (err) {
      closePopup();
      const data = err.response?.data;
      if (data instanceof Blob) {
        await alertBlobError(data);
      } else {
        alert(
          err.response?.data?.error ||
            err.message ||
            'Pakbon kon niet worden geladen. Controleer de Shopify-koppeling en of deze WooCommerce-order uit Shopify komt (mapping in shopify_woo_orders).'
        );
      }
    }
  };

  /**
   * Eerst PrintNode (zelfde cloud-print idee als pakbon bij orderafhandeling), daarna browser-fallback.
   */
  const openShopifyPackingSlip = async (wooOrderId) => {
    try {
      const printRes = await api.post(`/shopify-orders/packing-slip/${wooOrderId}/print`);
      const d = printRes.data;
      if (d?.success && d?.via === 'printnode') {
        return;
      }
    } catch (e) {
      const status = e.response?.status;
      if (status === 404) {
        alert(e.response?.data?.error || 'Geen pakbon');
        return;
      }
      if (status === 502) {
        const msg = e.response?.data?.error || 'PrintNode mislukt';
        if (!window.confirm(`${msg}\n\nPakbon in de browser openen?`)) return;
      }
    }
    await openShopifyPackingSlipInBrowser(wooOrderId);
  };

  const printShopifyPakbonnen = async () => {
    const ids = picklijst?.shopifyOrderIds || [];
    if (ids.length === 0) {
      alert('Geen Shopify-orders in deze picklijst.');
      return;
    }
    for (const wooId of ids) {
      await openShopifyPackingSlip(wooId);
    }
  };

  const handleVerzendkeuzeOk = async () => {
    if (!picklijst?.orderIds?.length) return;
    if (verzendenInFlightRef.current) return;
    verzendenInFlightRef.current = true;
    setVerzendenLoading(true);
    let errors = [];
    let success = 0;
    let partialShipped = 0;
    try {
      const shopifyIds = new Set(picklijst.shopifyOrderIds || []);
      const toWooId = (id) => (id && String(id).startsWith('wc-')) ? parseInt(String(id).replace('wc-', ''), 10) : parseInt(id, 10);
      const seenWoo = new Set();
      const uniqueOrderIds = (picklijst.orderIds || []).filter((orderId) => {
        const w = toWooId(orderId);
        if (!Number.isFinite(w) || w <= 0) return false;
        if (seenWoo.has(w)) return false;
        seenWoo.add(w);
        return true;
      });
      for (const orderId of uniqueOrderIds) {
        const wooId = toWooId(orderId);
        const order = findOrderInProductieByWooId(ordersInProductieRef.current, orderId);
        const shipped_line_items = buildShippedLineItemsForPickSession(order, picklijst);
        const isPartial = order
          ? computePartialAfterShipment(order, shipped_line_items) ||
            (isSingleOrderPicklijst && picklijstHasOpenUnpickedItems(picklijst?.items))
          : picklijstHasDeelzending || picklijstHasOpenUnpickedItems(picklijst?.items);
        if (shipped_line_items.length === 0) {
          errors.push(formatProcessOrderError(orderId, 'Geen gepickte regels om te verzenden'));
          continue;
        }
        try {
          const res = await api.post('/woocommerce/process-order', {
            order_id: orderId,
            carrier: verzendCarrier,
            service: verzendService,
            labels: Math.max(1, Math.min(10, parseInt(verzendLabels, 10) || 1)),
            auto_complete: isPartial ? 0 : 1,
            partial_shipment: isPartial ? 1 : 0,
            partial_note: isPartial
              ? order
                ? isSingleOrderPicklijst
                  ? buildPartialShipmentNoteFromPickedPicklijst(order, picklijst?.items)
                  : buildPartialShipmentNote(order)
                : buildPartialShipmentNoteFromPicklijst(picklijst)
              : undefined,
            shipped_line_items,
          });
          if (res.data?.success) {
            success++;
            if (res.data?.partial_shipment) partialShipped += 1;
            if (shopifyIds.has(wooId)) openShopifyPackingSlip(wooId);
          } else {
            errors.push(formatProcessOrderError(orderId, res.data?.error || 'Onbekend'));
          }
        } catch (err) {
          errors.push(formatProcessOrderError(orderId, err.response?.data?.error || err.message));
        }
      }
    } finally {
      verzendenInFlightRef.current = false;
      setVerzendenLoading(false);
    }
    setShowVerzendkeuze(false);
    setVerzendLabels('1');
    allesGepickt();
    const freshOrders = await fetchOrders();
    // Sidebar-badge "Magazijn" direct verversen i.p.v. wachten op de interval-poll.
    try {
      window.dispatchEvent(new Event('magazijn-orders-refresh'));
    } catch (_) { /* noop */ }
    const dagPickKlaar = success > 0 && errors.length === 0 && isMagazijnDagPickKlaar(freshOrders);
    if (success > 0) {
      markMagazijnHadAfhandelbareOrders();
    }
    if (errors.length > 0) {
      alert(`${success} order(s) verwerkt.\nFouten:\n${errors.join('\n')}`);
    } else if (dagPickKlaar) {
      setDagKlaarBannerDismissed(false);
      setDagKlaarMelding(
        buildMagazijnDagKlaarMeldingPayload(freshOrders, {
          verwerktCount: success,
          partialShipped,
        })
      );
      setDagKlaarBannerMelding(
        buildMagazijnDagKlaarMeldingPayload(freshOrders, {
          verwerktCount: success,
          partialShipped,
        })
      );
    } else if (partialShipped > 0) {
      alert(
        `${success} order(s) verwerkt (${partialShipped} deelzending). Labels en pakbonnen zijn geprint.\n\n` +
          'De order(s) blijven op In behandeling; resterende producten volgen zodra voorraad beschikbaar is.'
      );
    } else {
      alert(`${success} order(s) verwerkt. Labels en pakbonnen zijn geprint.`);
    }
  };

  const openScanner = (forSearch = false) => {
    setScanContext(!forSearch && picklijst ? MAGAZIJN_SCAN_CONTEXT : null);
    setShowScanner(true);
  };

  return (
    <div className="magazijn">
      <div className="magazijn-page-main">
      <header className="magazijn-header">
        <div className="magazijn-header-content">
          <h1>Magazijn</h1>
          <p className="magazijn-subtitle">Picklijsten en productlocaties</p>
        </div>
        <button
          type="button"
          className="magazijn-import-btn magazijn-import-btn-desktop"
          onClick={() => setShowLocatieImport(true)}
        >
          <FiUpload /> Locaties importeren
        </button>
      </header>

      <div className="magazijn-tabs">
        <button
          type="button"
          className={`magazijn-tab-btn ${activeTab === 'pick' ? 'active' : ''}`}
          onClick={() => setActiveTab('pick')}
        >
          Picklijsten
        </button>
        <button
          type="button"
          className={`magazijn-tab-btn ${activeTab === 'search' ? 'active' : ''}`}
          onClick={() => setActiveTab('search')}
        >
          Product zoeken
        </button>
        <button
          type="button"
          className={`magazijn-tab-btn ${activeTab === 'retouren' ? 'active' : ''}`}
          onClick={() => setActiveTab('retouren')}
        >
          Retouren
        </button>
      </div>

      {/* Orders in productie + picklijst */}
      {activeTab === 'pick' && (
      <section className="magazijn-picklijst-section">
        <h2 className="magazijn-section-title magazijn-title-row">
          <span>Picklijst</span>
          <div className="magazijn-title-actions">
            {!picklijst && (
              <span className="magazijn-section-count">
                {ordersInProductie.length} in behandeling • {orderFifoStats.afhandelbaarCount} af te handelen •{' '}
                {orderFifoStats.backorderCount} backorders
                {orderFifoStats.uitgesteld.length > 0
                  ? ` • ${orderFifoStats.uitgesteld.length} later verzenden`
                  : ''}
              </span>
            )}
            <button
              type="button"
              className="magazijn-scan-btn mag-hero-scan"
              onClick={() => openScanner(false)}
              title={picklijst ? 'Scan product voor picklijst' : 'Scan barcode'}
            >
              <FiCamera size={20} />
            </button>
          </div>
        </h2>
        {showDagKlaarBanner && dagKlaarBannerMelding ? (
          <div className="magazijn-dag-klaar-banner" role="status">
            <span className="magazijn-dag-klaar-banner-emoji" aria-hidden="true">
              {dagKlaarBannerMelding.emoji || '☕'}
            </span>
            <div className="magazijn-dag-klaar-banner-text">
              <strong className="magazijn-dag-klaar-banner-title">{dagKlaarBannerMelding.title}</strong>
              <p className="magazijn-dag-klaar-banner-body">{dagKlaarBannerMelding.body}</p>
              {dagKlaarBannerMelding.subtekst ? (
                <p className="magazijn-dag-klaar-banner-sub">{dagKlaarBannerMelding.subtekst}</p>
              ) : null}
            </div>
            <button
              type="button"
              className="magazijn-dag-klaar-banner-close"
              onClick={dismissDagKlaarBanner}
              title="Melding sluiten"
              aria-label="Melding sluiten"
            >
              ×
            </button>
          </div>
        ) : null}
        {picklijst ? (
          <div className="picklijst-container">
            <div className="picklijst-header">
              <div className="picklijst-header-top">
                <span className="picklijst-header-meta">
                  Orders: {picklijst.ordernummers.join(', ')} • {picklijst.items.length} producten • Scannen: <FiCamera size={14} aria-hidden />
                  {picklijst.orderVerzend?.some((x) => x.verzend_datum) ? (
                    <>
                      {' • '}
                      <span className="picklijst-header-verzend" title="Geplande verzenddatum per order">
                        Verzenden:{' '}
                        {picklijst.orderVerzend
                          .filter((x) => x.verzend_datum)
                          .map((x) => `${x.ordernummer} → ${x.verzend_datum_nl || formatVerzendDatumNl(x.verzend_datum)}`)
                          .join(' · ')}
                      </span>
                    </>
                  ) : null}
                </span>
                {picklijstVervoerderAdvies ? (
                  <span
                    className="picklijst-header-advies"
                    title="Zelfde verzendtype als op het WooCommerce-product (o.a. Leverbelofte-plugin voor levertijd op de site)."
                  >
                    {picklijstVervoerderAdvies}
                  </span>
                ) : (
                  <span
                    className="picklijst-header-advies picklijst-header-advies--placeholder"
                    title="Zet bij Product → Verzendtype (Standaard = DPD, DHL, Pallet). Webshop en voorraad moeten op elkaar aansluiten (SKU/EAN)."
                  >
                    {picklijstGeenVervoerderUitleg}
                  </span>
                )}
              </div>
              <div className="picklijst-header-actions">
                {picklijst.shopifyOrderIds?.length > 0 && (
                  <button
                    type="button"
                    className="picklijst-pakbon-btn"
                    onClick={printShopifyPakbonnen}
                    title="Print Shopify pakbon(nen) voor deze orders"
                  >
                    <FiPrinter size={16} /> Pakbon printen
                  </button>
                )}
                {canVerzendenPicklijst ? (
                  <button
                    type="button"
                    className={`picklijst-verzend-btn${picklijstHasDeelzending ? ' picklijst-verzend-btn--deel' : ''}`}
                    onClick={() => {
                      setVerzendLabels('1');
                      setShowVerzendkeuze(true);
                    }}
                  >
                    {picklijstHasDeelzending ? 'Deelzending verzenden' : 'Verzendkeuze (label + pakbon)'}
                  </button>
                ) : (
                  <button type="button" className="picklijst-done-btn" onClick={allesGepickt}>
                    <FiCheck /> Afbreken
                  </button>
                )}
              </div>
            </div>
            {picklijstHasDeelzending ? (
              <p className="magazijn-deelzending-hint">
                {isSingleOrderPicklijst ? (
                  <>
                    Deelorder: je hoeft niet alle regels te picken. Scan de regels die je <strong>nu</strong> wilt
                    verzenden; de rest blijft open. De order blijft op <strong>In behandeling</strong>.
                  </>
                ) : (
                  <>
                    Deelorder: scan alleen de producten <strong>met voorraad</strong>. Regels zonder voorraad hoeven niet
                    gescand; die volgen in een volgende zending. De order blijft op <strong>In behandeling</strong>.
                  </>
                )}
              </p>
            ) : null}
            <ol className="picklijst-items">
              {(() => {
                const withStock = picklijst.items.filter((i) => picklijstItemHasPickbareVoorraad(i));
                const noStock = picklijst.items.filter((i) => !picklijstItemHasPickbareVoorraad(i));
                const tePicken = withStock.filter(picklijstItemMoetNogGepicktWorden);
                const alVerzonden = withStock.filter((i) => !picklijstItemMoetNogGepicktWorden(i));
                const renderItem = (item, idx, numLabel) => {
                  const isBundel = item.is_bundel && Array.isArray(item.onderdelen) && item.onderdelen.length > 0;
                  const moetPicken = picklijstItemMoetNogGepicktWorden(item);
                  const scanned = item.scanned || 0;
                  const done = itemFullyPicked(item);
                  const canEditLocatie = item.product_id != null && moetPicken;
                  const pickbaar = picklijstItemHasPickbareVoorraad(item);
                  const pickTarget = picklijstItemOpenPickAantal(item);
                  const negatiefWoo = item.voorraad != null && item.voorraad < 0;

                  // Voortgangsindicator: bundel = aantal volledig gepickte onderdelen / totaal,
                  // anders gewoon scanned/aantal.
                  const bundelDoneCount = isBundel
                    ? item.onderdelen.filter((c) => (c.scanned || 0) >= (c.aantal || 0)).length
                    : 0;

                  return (
                    <li
                      key={`${item.product_id ?? ''}-${item.sku ?? ''}-${idx}`}
                      className={`picklijst-item ${done ? 'picklijst-item-done' : ''} ${!pickbaar ? 'picklijst-item-geen-voorraad' : ''} ${item.verzonden_volledig ? 'picklijst-item-al-verzonden' : ''} ${negatiefWoo && pickbaar ? 'picklijst-item-woo-backorder' : ''} ${isBundel ? 'picklijst-item-bundel' : ''}`}
                    >
                      <span className="picklijst-num">{numLabel}</span>
                      <div className="picklijst-locatie-cell">
                        {isBundel ? (
                          <span className="picklijst-bundel-badge" title="Bundelproduct: pick de onderdelen hieronder">
                            Bundel
                          </span>
                        ) : (
                          <>
                            <span className="picklijst-locatie-label">Locatie: </span>
                            <button
                              type="button"
                              className={`picklijst-locatie-value ${canEditLocatie ? 'picklijst-locatie-value-clickable' : ''}`}
                              onClick={() => canEditLocatie && startEditPicklijstLocatie(item)}
                              disabled={!canEditLocatie}
                              title={canEditLocatie ? 'Klik om locatie aan te passen' : undefined}
                            >
                              {item.magazijn_locatie || '—'}
                            </button>
                          </>
                        )}
                      </div>
                      <div className="picklijst-naam-block">
                        <span className="picklijst-naam">
                          <ProductNaamMetKlasse productId={item.product_id} naam={item.product_naam || 'Onbekend'} />
                          {item.verzonden_volledig ? (
                            <span className="picklijst-verzonden-badge">Verzonden</span>
                          ) : (item.shipped_qty || 0) > 0 ? (
                            <span className="picklijst-verzonden-badge picklijst-verzonden-badge--partial">
                              {item.shipped_qty}/{item.aantal} verzonden
                            </span>
                          ) : null}
                          {isBundel ? <span className="picklijst-bundel-x"> ×{item.aantal}</span> : null}
                        </span>
                        <span className={`picklijst-ean${item.ean ? '' : ' picklijst-ean--missing'}`}>
                          EAN: {item.ean || '— niet bekend'}
                        </span>
                        {!isBundel ? (
                          <div
                            className={`picklijst-totaal-voorraad${negatiefWoo ? ' picklijst-totaal-voorraad--negatief' : ''}`}
                            title={negatiefWoo ? 'WooCommerce toont negatieve voorraad (backorder/overselling). Order is wél te picken.' : undefined}
                          >
                            Totaal voorraad:{' '}
                            <strong>{item.voorraad !== undefined && item.voorraad !== null ? item.voorraad : '—'}</strong>
                            {negatiefWoo ? <span className="picklijst-backorder-hint"> · backorder in shop</span> : null}
                          </div>
                        ) : null}
                        {Array.isArray(item.product_options) && item.product_options.length > 0 && (
                          <div className="picklijst-product-options">
                            {item.product_options.map((opt, i) => (
                              <span key={i} className="picklijst-option">
                                {opt.key}: {String(opt.value)}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                      <span className="picklijst-meta">
                        {item.sku && `SKU: ${item.sku}`}
                      </span>
                      <span className="picklijst-aantal picklijst-scan-progress">
                        {!moetPicken ? (
                          <>
                            <FiCheck /> Verzonden
                          </>
                        ) : (
                          <>
                            {done ? <FiCheck /> : null}{' '}
                            {isBundel
                              ? `${bundelDoneCount}/${item.onderdelen.length} onderdelen`
                              : `${scanned}/${pickTarget}`}
                          </>
                        )}
                      </span>
                      {moetPicken && !done && !isBundel && (
                        <button
                          type="button"
                          className="picklijst-handmatig-btn"
                          onClick={() => handleManualPicked(item)}
                          title="Handmatig als gepikt markeren (als scanner niet werkt)"
                        >
                          Handmatig gepikt
                        </button>
                      )}
                      {isBundel && (
                        <ul className="picklijst-bundel-children">
                          {item.onderdelen.map((child, ci) => {
                            const cScanned = child.scanned || 0;
                            const cDone = cScanned >= (child.aantal || 0);
                            const cNeg = child.voorraad != null && child.voorraad < 0;
                            const cVoorraadGoed =
                              child.voorraad == null
                              || child.voorraad < 0
                              || child.voorraad >= (child.aantal || 0);
                            return (
                              <li
                                key={`${child.product_id ?? ''}-${child.sku ?? ''}-${ci}`}
                                className={`picklijst-bundel-child ${cDone ? 'picklijst-bundel-child-done' : ''} ${!cVoorraadGoed ? 'picklijst-bundel-child-geen-voorraad' : ''}`}
                              >
                                <span className="picklijst-bundel-child-locatie">
                                  {child.magazijn_locatie || '—'}
                                </span>
                                <span className="picklijst-bundel-child-naam">
                                  <ProductNaamMetKlasse productId={child.product_id} naam={child.product_naam || 'Onbekend'} />
                                  {child.aantal_per_parent && child.aantal_per_parent > 1 ? (
                                    <span className="picklijst-bundel-child-perparent">
                                      {' '}({child.aantal_per_parent}× per bundel)
                                    </span>
                                  ) : null}
                                  <span className={`picklijst-bundel-child-ean${child.ean ? '' : ' picklijst-ean--missing'}`}>
                                    {' '}
                                    · EAN {child.ean || '— niet bekend'}
                                  </span>
                                  <span className="picklijst-bundel-child-voorraad">
                                    {' · voorraad '}
                                    <strong>{child.voorraad != null ? child.voorraad : '—'}</strong>
                                    {cNeg ? ' (backorder)' : ''}
                                  </span>
                                </span>
                                <span className="picklijst-bundel-child-progress">
                                  {cDone ? <FiCheck /> : null} {cScanned}/{child.aantal}
                                </span>
                                {!cDone && (
                                  <button
                                    type="button"
                                    className="picklijst-handmatig-btn picklijst-handmatig-btn-small"
                                    onClick={() => handleManualPickedChild(item, child)}
                                    title="Onderdeel handmatig als gepikt markeren"
                                  >
                                    Gepikt
                                  </button>
                                )}
                              </li>
                            );
                          })}
                        </ul>
                      )}
                    </li>
                  );
                };
                let num = 0;
                return (
                  <>
                    {tePicken.map((item, idx) => {
                      num += 1;
                      return renderItem(item, idx, num);
                    })}
                    {alVerzonden.length > 0 && (
                      <>
                        <li className="picklijst-section-heading picklijst-section-heading--verzonden" aria-hidden="true">
                          <span className="picklijst-section-heading-text">
                            Al verzonden – niet meer picken ({alVerzonden.length})
                          </span>
                        </li>
                        {alVerzonden.map((item, idx) => {
                          num += 1;
                          return renderItem(item, tePicken.length + idx, num);
                        })}
                      </>
                    )}
                    {noStock.length > 0 && (
                      <>
                        <li className="picklijst-section-heading" aria-hidden="true">
                          <span className="picklijst-section-heading-text">Geen voorraad – niet te picken</span>
                        </li>
                        {noStock.map((item, idx) => {
                          num += 1;
                          return renderItem(item, tePicken.length + alVerzonden.length + idx, num);
                        })}
                      </>
                    )}
                  </>
                );
              })()}
            </ol>
          </div>
        ) : (
          <>
            {ordersError && (
              <p className="magazijn-empty magazijn-error">
                {ordersError} <button type="button" className="magazijn-refresh-link" onClick={fetchOrders}>Opnieuw proberen</button>
              </p>
            )}
            {ordersLoading ? (
              <p className="magazijn-empty">Orders ophalen...</p>
            ) : ordersInProductie.length === 0 && !ordersError ? (
              <p className="magazijn-empty">
                Geen openstaande webshop orders. Tonen: &quot;In behandeling&quot;, &quot;In afwachting&quot; en &quot;In afwachting van betaling&quot; – <button type="button" className="magazijn-refresh-link" onClick={fetchOrders}>vernieuwen</button>.
              </p>
            ) : (
              <>
                <div className="picklijst-orders-header">
                  <label className="picklijst-mode-toggle">
                    <input
                      type="checkbox"
                      checked={perOrderMode}
                      onChange={(e) => setPerOrderMode(e.target.checked)}
                    />
                    <span>Per order afhandelen</span>
                    <span
                      className="picklijst-mode-toggle-hint"
                      title="Aan: alleen het orderoverzicht (per order). Uit: producten gebundeld over alle orders + knop voor één picklijst."
                    >
                      (standaard aan)
                    </span>
                  </label>
                  <button type="button" className="magazijn-refresh-btn" onClick={fetchOrders} disabled={ordersLoading}>
                    Vernieuwen
                  </button>
                </div>
                {perOrderMode ? (
                  <ul className="picklijst-orders picklijst-orders-per-stuk">
                    {(() => {
                      const { pickbaar, uitgesteld, noStockIds } = orderFifoStats;
                      const ordersWithStock = pickbaar.filter((o) => !noStockIds.has(o.id));
                      const ordersNoStock = pickbaar.filter((o) => noStockIds.has(o.id));
                      const renderOrderCard = (o, { isUitgesteld = false } = {}) => {
                        const draftDate = verzendDatumDraft[o.id] ?? o.verzend_datum ?? '';
                        const saving = verzendDatumSaving === o.id;
                        const noteSnippet = truncateOrderNote(o.customer_note || o.verzend_notitie);
                        const showVerzendEditor = isUitgesteld || verzendDatumOpen[o.id] === true;
                        const toggleVerzendEditor = (open) => {
                          setVerzendDatumOpen((prev) => ({ ...prev, [o.id]: open }));
                          if (!open) {
                            setVerzendDatumDraft((prev) => {
                              const next = { ...prev };
                              delete next[o.id];
                              return next;
                            });
                          }
                        };
                        return (
                          <li
                            key={o.id}
                            className={`picklijst-order-card${isUitgesteld ? ' picklijst-order-card--uitgesteld' : ''}`}
                          >
                            <div className="picklijst-order-card-top">
                              <div className="picklijst-order-card-info">
                                <div className="picklijst-order-card-title-row">
                                  <strong>{o.ordernummer}</strong>
                                  {' – '}{o.klant_naam || 'Klant'}
                                  {(o.regels || []).length > 0 && (
                                    <span className="picklijst-order-regels">
                                      {' '}
                                      ({(o.regels || []).length} regels)
                                    </span>
                                  )}
                                  {formatOrderDateTime(o.date_created) && (
                                    <span
                                      className="picklijst-order-datetime"
                                      title="Datum en tijd waarop de order is geplaatst (voor/na cut-off)"
                                    >
                                      {' · '}{formatOrderDateTime(o.date_created)}
                                    </span>
                                  )}
                                  {o.verzend_datum ? (
                                    <span className="picklijst-verzend-badge" title="Geplande verzenddatum">
                                      {' · '}Verzenden {formatVerzendDatumNl(o.verzend_datum)}
                                    </span>
                                  ) : null}
                                </div>
                                {noteSnippet ? (
                                  <p className="picklijst-order-note" title={o.customer_note || o.verzend_notitie || undefined}>
                                    Opmerking: {noteSnippet}
                                  </p>
                                ) : null}
                                {isUitgesteld && o.pick_uitgesteld_reden ? (
                                  <p className="picklijst-uitgesteld-reden">{o.pick_uitgesteld_reden}</p>
                                ) : null}
                                {!isUitgesteld ? (
                                  <label className="picklijst-verzend-toggle">
                                    <input
                                      type="checkbox"
                                      checked={showVerzendEditor}
                                      onChange={(e) => toggleVerzendEditor(e.target.checked)}
                                    />
                                    <span>Later verzenden</span>
                                  </label>
                                ) : null}
                              </div>
                              <div className="picklijst-order-card-actions">
                                {o.is_shopify && (
                                  <div className="picklijst-order-card-pakbon-row">
                                    <span className="picklijst-order-shopify-badge" title="Shopify order – pakbon wordt automatisch geprint">
                                      S
                                    </span>
                                    <button
                                      type="button"
                                      className="picklijst-pakbon-btn picklijst-pakbon-btn-inline"
                                      onClick={() => openShopifyPackingSlip(typeof o.id === 'string' && o.id.startsWith('wc-') ? parseInt(o.id.replace('wc-', ''), 10) : o.id)}
                                      title="Print Shopify pakbon"
                                    >
                                      <FiPrinter size={14} /> Pakbon
                                    </button>
                                  </div>
                                )}
                                <button
                                  type="button"
                                  className={`picklijst-start-btn${isUitgesteld ? ' picklijst-start-btn--disabled' : ''}`}
                                  onClick={() => maakPicklijst(o)}
                                  disabled={isUitgesteld}
                                  title={isUitgesteld ? o.pick_uitgesteld_reden || 'Nog niet vrijgegeven' : undefined}
                                >
                                  <FiList /> Start picklijst
                                </button>
                              </div>
                            </div>
                            {showVerzendEditor ? (
                              <div className="picklijst-order-verzend-row">
                                <label className="picklijst-verzend-label" htmlFor={`verzend-${o.id}`}>
                                  Verzenddatum
                                </label>
                                <input
                                  id={`verzend-${o.id}`}
                                  type="date"
                                  className="picklijst-verzend-input"
                                  value={draftDate}
                                  onChange={(e) =>
                                    setVerzendDatumDraft((prev) => ({ ...prev, [o.id]: e.target.value }))
                                  }
                                />
                                <button
                                  type="button"
                                  className="picklijst-verzend-save-btn"
                                  disabled={saving}
                                  onClick={() => saveOrderVerzendDatum(o, draftDate || null)}
                                >
                                  {saving ? 'Opslaan…' : 'Opslaan'}
                                </button>
                                {o.verzend_datum ? (
                                  <button
                                    type="button"
                                    className="picklijst-verzend-clear-btn"
                                    disabled={saving}
                                    onClick={async () => {
                                      await saveOrderVerzendDatum(o, null);
                                      if (!isUitgesteld) toggleVerzendEditor(false);
                                    }}
                                  >
                                    Wissen
                                  </button>
                                ) : null}
                              </div>
                            ) : null}
                          </li>
                        );
                      };
                      return (
                        <>
                          {ordersWithStock.length > 0 ? (
                            <>
                              <li className="picklijst-orders-section-heading picklijst-orders-section-heading--ready" key="heading-ready">
                                <span className="picklijst-orders-section-heading-text">
                                  Af te handelen ({ordersWithStock.length})
                                </span>
                              </li>
                              {ordersWithStock.map((o) => renderOrderCard(o))}
                            </>
                          ) : null}
                          {ordersNoStock.length > 0 && (
                            <>
                              <li className="picklijst-orders-section-heading" key="heading-no-stock">
                                <span className="picklijst-orders-section-heading-text">Orders zonder (voldoende) voorraad ({ordersNoStock.length})</span>
                              </li>
                              {ordersNoStock.map((o) => renderOrderCard(o))}
                            </>
                          )}
                          {uitgesteld.length > 0 && (
                            <>
                              <li className="picklijst-orders-section-heading picklijst-orders-section-heading--uitgesteld" key="heading-uitgesteld">
                                <span className="picklijst-orders-section-heading-text">
                                  Later verzenden ({uitgesteld.length}) — vrij werkdag vóór verzending na{' '}
                                  {uitgesteld[0]?.pick_vrijgave_uur ?? 15}:00
                                </span>
                              </li>
                              {uitgesteld.map((o) => renderOrderCard(o, { isUitgesteld: true }))}
                            </>
                          )}
                        </>
                      );
                    })()}
                  </ul>
                ) : picklijstPayloadAlleOrders.items.length > 0 ? (
                  <div className="picklijst-samenvatting-alles picklijst-samenvatting-alles--alleen-bulk">
                    <h3 className="picklijst-samenvatting-titel">
                      Totaal over {ordersPickbaarNu.length} pickbare order{ordersPickbaarNu.length === 1 ? '' : 's'}
                      {picklijstOrderSplit.uitgesteld.length > 0
                        ? ` (${picklijstOrderSplit.uitgesteld.length} later verzenden)`
                        : ''}{' '}
                      — {picklijstPayloadAlleOrders.items.length} productregel
                      {picklijstPayloadAlleOrders.items.length === 1 ? '' : 's'}
                    </h3>
                    <p className="picklijst-samenvatting-uitleg">
                      Zet <strong>Per order afhandelen</strong> weer aan voor het normale orderoverzicht. Of start hieronder
                      één picklijst met alle nu pickbare orders (uitgestelde orders onderaan worden overgeslagen).
                    </p>
                    <ul className="picklijst-samenvatting-lijst">
                      {picklijstPayloadAlleOrders.items.map((it) => {
                        const ok = picklijstItemHasPickbareVoorraad(it);
                        return (
                          <li
                            key={picklijstSamenvattingItemKey(it)}
                            className={`picklijst-samenvatting-regel ${!ok ? 'picklijst-samenvatting-regel--geen-voorraad' : ''}`}
                          >
                            <span className="picklijst-sam-loc" title="Magazijnlocatie">
                              {it.magazijn_locatie?.trim() ? it.magazijn_locatie : '—'}
                            </span>
                            <span className="picklijst-sam-naam">
                              <ProductNaamMetKlasse productId={it.product_id} naam={it.product_naam || '—'} />
                              {it.is_bundel ? (
                                <span className="picklijst-sam-bundel" title="Bundel — onderdelen worden in de picklijst per stuk getoond">
                                  Bundel
                                </span>
                              ) : null}
                            </span>
                            <span className="picklijst-sam-sku" title="SKU / EAN">
                              {[it.sku, it.ean].filter(Boolean).join(' · ') || '—'}
                            </span>
                            <span className="picklijst-sam-aantal">{it.aantal}×</span>
                          </li>
                        );
                      })}
                    </ul>
                    <div className="picklijst-samenvatting-bulk-actie">
                      <button type="button" className="picklijst-maak-btn" onClick={maakPicklijstVanAlleOrders}>
                        <FiList /> Maak picklijst van alle orders
                      </button>
                    </div>
                  </div>
                ) : (
                  <p className="magazijn-empty picklijst-samenvatting-leeg">Geen productregels in deze orders.</p>
                )}
              </>
            )}
          </>
        )}
      </section>
      )}

      {activeTab === 'search' && (
        <>
          <div className="magazijn-search-row">
            <SearchInputWithHistory
              variant="magazijn"
              inputRef={searchInputRef}
              placeholder="Zoek op EAN, SKU of productnaam..."
            />
            <button
              type="button"
              className="magazijn-scan-btn"
              onClick={() => openScanner(true)}
              title="Scan barcode"
            >
              <FiCamera size={24} />
            </button>
          </div>

          {loading && <div className="magazijn-loading">Zoeken...</div>}

          {!selectedProduct && results.length > 0 && (
            <ul className="magazijn-results">
              {results.map((p) => (
                <li
                  key={p.id}
                  className="magazijn-result-item"
                  onClick={() => selectProduct(p)}
                >
                  <div className="magazijn-result-naam">{p.naam}</div>
                  <div className="magazijn-result-meta">
                    {p.sku && <span>SKU: {p.sku}</span>}
                    {p.ean && <span>EAN: {p.ean}</span>}
                    {p.voorraad != null && <span>Voorraad: {p.voorraad}</span>}
                  </div>
                  <div className="magazijn-result-locatie">
                    <FiMapPin size={14} />
                    {p.magazijn_locatie || 'Geen locatie'}
                  </div>
                </li>
              ))}
            </ul>
          )}

          {!selectedProduct && searchTerm.trim().length >= 2 && !loading && results.length === 0 && (
            <div className="magazijn-empty">Geen producten gevonden</div>
          )}

          {selectedProduct && (
            <div className="magazijn-detail">
              <div className="magazijn-detail-top-row">
                <button type="button" className="magazijn-back" onClick={clearSelection}>
                  ← Terug
                </button>
                <button
                  type="button"
                  className="btn btn-secondary magazijn-label-print-btn"
                  onClick={handlePrintMagazijnProductLabel}
                  disabled={!getBarcodeSpec(selectedProduct)}
                  title={
                    getBarcodeSpec(selectedProduct)
                      ? 'Drukt een 10×15 cm label af (papierformaat instellen in het printvenster).'
                      : 'Dit product heeft geen EAN of SKU voor de streepjescode.'
                  }
                >
                  <FiPrinter /> Label 10×15
                </button>
              </div>
              <div className="magazijn-detail-product">
                <h2>{selectedProduct.naam}</h2>
                <div className="magazijn-detail-meta">
                  {selectedProduct.sku && <span>SKU: {selectedProduct.sku}</span>}
                  {selectedProduct.ean && <span>EAN: {selectedProduct.ean}</span>}
                </div>
                <div className="magazijn-detail-voorraad-row">
                  {selectedProduct.voorraad != null ? (
                    <span
                      className="magazijn-detail-voorraad"
                      title="Vrij voor nieuwe verkoop (Woo-voorraad, gesynchroniseerd)"
                    >
                      Beschikbare voorraad: <strong>{selectedProduct.voorraad}</strong>
                    </span>
                  ) : null}
                  <span
                    className="magazijn-detail-voorraad magazijn-detail-voorraad--aanwezig"
                    title="Beschikbaar + stuks in open webshop-orders (processing)"
                  >
                    Aanwezige voorraad:{' '}
                    <strong>
                      {selectedProductOpenVoorraadLoading
                        ? '…'
                        : (parseInt(selectedProduct.voorraad, 10) || 0) +
                          (selectedProductOpenVoorraad?.in_behandeling ?? 0)}
                    </strong>
                    {!selectedProductOpenVoorraadLoading &&
                    (selectedProductOpenVoorraad?.in_behandeling ?? 0) > 0 ? (
                      <span className="magazijn-detail-voorraad-sub">
                        {' '}
                        ({selectedProductOpenVoorraad.in_behandeling} in orders)
                      </span>
                    ) : null}
                  </span>
                  {!selectedProductOpenVoorraadLoading &&
                  (selectedProductOpenVoorraad?.backorders ?? 0) > 0 ? (
                    <span
                      className="magazijn-detail-voorraad magazijn-detail-voorraad--backorder"
                      title="Nog niet toewijsbaar via FIFO"
                    >
                      Backorders: <strong>{selectedProductOpenVoorraad.backorders}</strong>
                    </span>
                  ) : null}
                </div>
              </div>
              <div className="magazijn-locatie-form">
                <label htmlFor="locatie-input">
                  <FiMapPin /> Magazijnlocatie
                </label>
                <input
                  ref={locatieInputRef}
                  id="locatie-input"
                  type="text"
                  className="magazijn-locatie-input"
                  placeholder="bijv. A-12-3, Rek B plank 4..."
                  value={locatieInput}
                  onChange={(e) => setLocatieInput(e.target.value)}
                />
                <button
                  type="button"
                  className="magazijn-save-btn"
                  onClick={saveLocatie}
                  disabled={saving}
                >
                  {saving ? (
                    'Opslaan...'
                  ) : saved ? (
                    <>
                      <FiCheck /> Opgeslagen!
                    </>
                  ) : (
                    <>
                      <FiMapPin /> Locatie opslaan
                    </>
                  )}
                </button>
              </div>
            </div>
          )}
        </>
      )}

      {activeTab === 'retouren' && (
        <section className="magazijn-retouren">
          <div className="retouren-toolbar">
            <div className="retouren-filters">
              <label htmlFor="retouren-status-filter">Status:</label>
              <select
                id="retouren-status-filter"
                value={retourenStatusFilter}
                onChange={(e) => setRetourenStatusFilter(e.target.value)}
              >
                <option value="">Alle</option>
                <option value="aangemeld">Aangemeld</option>
                <option value="ontvangen">Ontvangen</option>
                <option value="terugbetaald">Terugbetaald</option>
                <option value="geweigerd">Geweigerd</option>
              </select>
              <span className="retouren-verversen-met-badge">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={() => fetchRetouren(retourenStatusFilter)}
                  disabled={retourenLoading}
                >
                  Verversen
                </button>
                {retourenBadgeCount != null && retourenBadgeCount > 0 ? (
                  <span className="magazijn-tab-badge retouren-toolbar-open-badge" title="Openstaande retouren (nog te verwerken)">
                    {retourenBadgeCount > 99 ? '99+' : retourenBadgeCount}
                  </span>
                ) : null}
              </span>
            </div>
            <button type="button" className="btn btn-primary" onClick={openNewRetourModal}>
              <FiPlus /> Nieuwe retour
            </button>
          </div>

          {retourenLoading ? (
            <div className="retouren-empty">Laden…</div>
          ) : retouren.length === 0 ? (
            <div className="retouren-empty">
              Nog geen retouren. Klik op <strong>Nieuwe retour</strong> om een retour aan te melden.
            </div>
          ) : retourenGefilterd.length === 0 ? (
            <div className="retouren-empty">
              Geen retouren gevonden voor <strong>{searchTerm.trim()}</strong>. Pas de zoekterm of het
              statusfilter aan.
            </div>
          ) : (
            <ul className="picklijst-orders picklijst-orders-per-stuk retouren-kaarten">
              {(() => {
                const open = retourenGefilterd.filter((r) => retourKanNogVerwerktWorden(r));
                const afgerond = retourenGefilterd.filter((r) => !retourKanNogVerwerktWorden(r));
                const renderRetourCard = (r) => {
                  const teruglegLocaties = formatTeruglegLocaties(r.terugleg_locaties);
                  const toonTerugleg = retourIsTerugbetaald(r) && teruglegLocaties;
                  return (
                  <li
                    key={r.id}
                    className={`picklijst-order-card retour-order-card ${retourKanNogVerwerktWorden(r) ? '' : 'retour-order-card--afgerond'}`}
                  >
                    <div className="picklijst-order-card-info">
                      <strong>{r.retournummer}</strong>
                      {' · order '}
                      <strong>{r.ordernummer}</strong>
                      <span className="retour-card-meta">
                        {' '}
                        ({r.totaal_aangemeld || 0} stuks · {formatEuro(r.refund_bedrag)})
                      </span>
                      <div className="retour-card-klant">
                        {r.klant_naam || 'Klant'}{' '}
                        {r.klant_postcode ? (
                          <span className="retour-card-postcode" title="Postcode klant">
                            · {r.klant_postcode}
                          </span>
                        ) : null}
                        {r.klant_email ? (
                          <span className="retour-card-email" title={r.klant_email}>
                            &lt;{r.klant_email}&gt;
                          </span>
                        ) : null}
                      </div>
                      {toonTerugleg ? (
                        <div className="retour-card-terugleg" title="Magazijnlocatie om product terug te leggen">
                          <FiMapPin size={14} aria-hidden />
                          Terugleggen op: <strong>{teruglegLocaties}</strong>
                        </div>
                      ) : null}
                      <div className="retour-card-regel2">
                        <span className={`retour-status-badge status-${r.status}`}>{retourStatusLabel(r.status)}</span>
                        {formatRetourDatum(r.aangemeld_at) ? (
                          <span className="retour-card-datum" title="Aangemeld">
                            {' · '}
                            {formatRetourDatum(r.aangemeld_at)}
                          </span>
                        ) : null}
                      </div>
                    </div>
                    <div className="picklijst-order-card-actions retour-order-card-actions">
                      <button
                        type="button"
                        className="btn btn-secondary retour-card-btn-details"
                        onClick={() => openRetourDetail(r.id)}
                      >
                        Details
                      </button>
                      {retourKanNogVerwerktWorden(r) ? (
                        <button
                          type="button"
                          className="picklijst-start-btn"
                          onClick={() => openRetourOntvangst(r.id)}
                          title="Ontvangst registreren, controleren en terugbetalen"
                        >
                          <FiPackage size={16} aria-hidden />
                          Verwerk retour
                        </button>
                      ) : null}
                    </div>
                  </li>
                  );
                };
                return (
                  <>
                    {open.map(renderRetourCard)}
                    {afgerond.length > 0 && (
                      <>
                        <li className="picklijst-orders-section-heading" key="retouren-heading-afgerond">
                          <span className="picklijst-orders-section-heading-text">
                            Afgerond ({afgerond.length})
                          </span>
                        </li>
                        {afgerond.map(renderRetourCard)}
                      </>
                    )}
                  </>
                );
              })()}
            </ul>
          )}
        </section>
      )}

      {showScanner && (
        <BarcodeScanner
          onScan={(code) => {
            if (scanContext === MAGAZIJN_SCAN_CONTEXT && picklijst) {
              handlePicklijstScan(code);
            } else if (scanContext === MAGAZIJN_RETOUR_ONTVANGST) {
              doRetourOntvangstScan(code);
            } else {
              setSearchTerm(code);
              setShowScanner(false);
            }
          }}
          onClose={() => {
            setShowScanner(false);
            setScanContext(null);
          }}
        />
      )}

      <Modal
        show={!!dagKlaarMelding}
        onClose={() => setDagKlaarMelding(null)}
        title=""
        contentClassName="magazijn-dag-klaar-modal"
      >
        <div className="magazijn-dag-klaar">
          <div className="magazijn-dag-klaar-emoji" aria-hidden="true">
            {dagKlaarMelding?.emoji || '☕'}
          </div>
          <h3 className="magazijn-dag-klaar-title">{dagKlaarMelding?.title}</h3>
          <p className="magazijn-dag-klaar-body">{dagKlaarMelding?.body}</p>
          {dagKlaarMelding?.verwerktCount > 0 ? (
            <p className="magazijn-dag-klaar-meta">
              {dagKlaarMelding.verwerktCount} order{dagKlaarMelding.verwerktCount === 1 ? '' : 's'} verwerkt
              {dagKlaarMelding.partialShipped > 0
                ? ` (${dagKlaarMelding.partialShipped} deelzending${dagKlaarMelding.partialShipped === 1 ? '' : 'en'})`
                : ''}
              . Labels en pakbonnen zijn geprint.
            </p>
          ) : null}
          {dagKlaarMelding?.subtekst ? (
            <p className="magazijn-dag-klaar-sub">{dagKlaarMelding.subtekst}</p>
          ) : null}
          <button
            type="button"
            className="btn btn-primary magazijn-dag-klaar-btn"
            onClick={() => setDagKlaarMelding(null)}
          >
            Op naar de koffie ☕
          </button>
        </div>
      </Modal>

      <Modal
        show={showVerzendkeuze}
        onClose={() => {
          if (verzendenLoading) return;
          setShowVerzendkeuze(false);
          setVerzendLabels('1');
        }}
        title={
          picklijstHasDeelzending
            ? picklijst?.ordernummers?.length === 1
              ? `Deelzending – Order ${picklijst.ordernummers[0]}`
              : 'Deelzending'
            : picklijst?.ordernummers?.length === 1
              ? `Verzendkeuze – Order ${picklijst.ordernummers[0]}`
              : 'Verzendkeuze'
        }
      >
        <div style={{ padding: '1rem 0' }}>
          {picklijstHasDeelzending ? (
            <p className="magazijn-deelzending-hint">
              <strong>Deelzending:</strong>{' '}
              {isSingleOrderPicklijst
                ? 'alleen de volledig gepickte regels worden als verzonden gemarkeerd. Resterende regels blijven open; de order blijft op In behandeling.'
                : 'alleen producten met voorraad zijn gepickt. Na verzenden blijft de order op In behandeling in WooCommerce. Resterende regels volgen later.'}{' '}
              Pakbon/label zijn nog voor de hele order (fase 1).
            </p>
          ) : null}
          <p style={{ marginBottom: '1rem', color: '#666' }}>
            Kies de verzendmethode. Na OK worden labels en pakbonnen automatisch geprint via de Order Afhandeling plugin.
          </p>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>Carrier</label>
            <select
              value={verzendCarrier}
              onChange={(e) => {
                const c = e.target.value;
                setVerzendCarrier(c);
                setVerzendService(c === 'dpd' ? 'DPD HOME' : 'DHL Particulier - Thuis bezorgen - Pakket medium');
              }}
              style={{ width: '100%', padding: '0.5rem' }}
            >
              <option value="dpd">DPD</option>
              <option value="dhl">DHL</option>
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>Service</label>
            <select
              value={verzendService}
              onChange={(e) => setVerzendService(e.target.value)}
              style={{ width: '100%', padding: '0.5rem' }}
            >
              {verzendCarrier === 'dpd' ? (
                <>
                  <option value="DPD HOME">DPD HOME</option>
                  <option value="DPD HOME - Saturday delivery">DPD HOME - Saturday delivery</option>
                </>
              ) : (
                <>
                  <option value="DHL Particulier - Thuis bezorgen - Pakket medium">DHL Particulier - Thuis bezorgen - Pakket medium</option>
                  <option value="DHL Zakelijk - Thuis bezorgen - Pakket medium">DHL Zakelijk - Thuis bezorgen - Pakket medium</option>
                </>
              )}
            </select>
          </div>
          <div className="form-group" style={{ marginBottom: '1rem' }}>
            <label>Aantal verzendlabels</label>
            <div className="labels-stepper">
              <button
                type="button"
                className="labels-stepper-btn"
                onClick={() => setVerzendLabels((v) => String(Math.max(1, (parseInt(v, 10) || 1) - 1)))}
              >
                <FiMinus />
              </button>
              <input
                type="number"
                min={1}
                max={10}
                value={verzendLabels}
                onChange={(e) => setVerzendLabels(e.target.value)}
                onBlur={(e) => {
                  const n = parseInt(e.target.value, 10);
                  if (isNaN(n) || n < 1) setVerzendLabels('1');
                  else setVerzendLabels(String(Math.min(10, n)));
                }}
                className="labels-stepper-input"
              />
              <button
                type="button"
                className="labels-stepper-btn"
                onClick={() => setVerzendLabels((v) => String(Math.min(10, (parseInt(v, 10) || 1) + 1)))}
              >
                <FiPlus />
              </button>
            </div>
            <p className="magazijn-labels-hint" style={{ margin: '0.5rem 0 0', fontSize: '0.85rem', color: '#64748b', lineHeight: 1.4 }}>
              Dit getal gaat als <code style={{ fontSize: '0.8em' }}>labels</code> naar Order Afhandeling (aantal <strong>colli</strong> richting DPD/DHL in
              de plugin). Zie je in PrintNode <strong>twee keer</strong> hetzelfde label én dezelfde pakbon, dan is <code>process-order</code> waarschijnlijk
              <strong>twee keer</strong> gelopen (dubbele order in de picklijst, dubbele actie, of de carrier-plugin print óók zelf) — niet omdat de
              plugin <code>labels</code> keer op keer de hele printstap herhaalt.
            </p>
          </div>
          <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1.5rem' }}>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleVerzendkeuzeOk}
              disabled={verzendenLoading}
            >
              {verzendenLoading ? 'Verwerken...' : picklijstHasDeelzending ? 'OK – deelzending printen' : 'OK – print label + pakbon'}
            </button>
            <button
              type="button"
              className="btn btn-secondary"
              onClick={() => {
                setShowVerzendkeuze(false);
                setVerzendLabels('1');
              }}
              disabled={verzendenLoading}
            >
              Annuleren
            </button>
          </div>
        </div>
      </Modal>

      <Modal
        show={picklijstEditLocatieKey != null}
        onClose={() => !picklijstLocatieSaving && cancelEditPicklijstLocatie()}
        title="Locatie aanpassen"
      >
        {(() => {
          const editItem = picklijst?.items?.find((i) => itemKey(i) === picklijstEditLocatieKey);
          if (!editItem) return null;
          return (
            <div className="picklijst-locatie-modal-body">
              <p className="picklijst-locatie-modal-product">
                <ProductNaamMetKlasse productId={editItem.product_id} naam={editItem.product_naam || 'Onbekend'} strong />
                {editItem.sku && <span className="picklijst-locatie-modal-sku"> SKU: {editItem.sku}</span>}
              </p>
              <div className="form-group" style={{ marginBottom: '1.25rem' }}>
                <label htmlFor="picklijst-locatie-input">Locatie</label>
                <input
                  id="picklijst-locatie-input"
                  type="text"
                  className="picklijst-locatie-modal-input"
                  value={picklijstEditLocatieValue}
                  onChange={(e) => setPicklijstEditLocatieValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') savePicklijstLocatie(editItem, picklijstEditLocatieValue);
                    if (e.key === 'Escape') cancelEditPicklijstLocatie();
                  }}
                  disabled={picklijstLocatieSaving}
                  autoFocus
                  placeholder="bijv. A-12-3"
                />
              </div>
              <div style={{ display: 'flex', gap: '0.75rem', marginTop: '1.5rem' }}>
                <button
                  type="button"
                  className="btn btn-primary"
                  onClick={() => savePicklijstLocatie(editItem, picklijstEditLocatieValue)}
                  disabled={picklijstLocatieSaving}
                >
                  {picklijstLocatieSaving ? 'Opslaan...' : 'Opslaan'}
                </button>
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={cancelEditPicklijstLocatie}
                  disabled={picklijstLocatieSaving}
                >
                  Annuleren
                </button>
              </div>
            </div>
          );
        })()}
      </Modal>

      <Modal
        show={showLocatieImport}
        onClose={() => {
          setShowLocatieImport(false);
          setLocatieImportData('');
        }}
        title="Locaties importeren"
      >
        <div className="magazijn-import-tabs">
          <button
            type="button"
            className={`btn ${locatieImportMode === 'paste' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setLocatieImportMode('paste')}
          >
            Plak uit Excel
          </button>
          <button
            type="button"
            className={`btn ${locatieImportMode === 'file' ? 'btn-primary' : 'btn-secondary'}`}
            onClick={() => setLocatieImportMode('file')}
          >
            Upload Excel
          </button>
        </div>
        {locatieImportMode === 'paste' ? (
          <>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              Format: EAN en locatie per regel, gescheiden door tab, komma of puntkomma.
            </p>
            <textarea
              value={locatieImportData}
              onChange={(e) => setLocatieImportData(e.target.value)}
              placeholder="8721001690862  A-12-3&#10;8721001691791  Rek B plank 4"
              rows={8}
              style={{ width: '100%', padding: '0.75rem', fontFamily: 'inherit', marginBottom: '1rem' }}
            />
          </>
        ) : (
          <>
            <p style={{ fontSize: '0.9rem', color: '#666', marginBottom: '1rem' }}>
              Kolommen: EAN/Barcode en Locatie (of vergelijkbare namen).
            </p>
            <input
              type="file"
              accept=".xlsx,.xls"
              onChange={handleLocatieFileUpload}
              disabled={locatieImportLoading}
              style={{ padding: '0.5rem' }}
            />
          </>
        )}
        <div style={{ display: 'flex', gap: '0.5rem', marginTop: '1rem' }}>
          <button
            type="button"
            className="btn btn-secondary"
            onClick={() => {
              setShowLocatieImport(false);
              setLocatieImportData('');
            }}
            disabled={locatieImportLoading}
          >
            Annuleren
          </button>
          {locatieImportMode === 'paste' && (
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleLocatiePasteImport}
              disabled={locatieImportLoading || !locatieImportData.trim()}
            >
              <FiUpload /> {locatieImportLoading ? 'Importeren...' : 'Importeren'}
            </button>
          )}
        </div>
      </Modal>

      <Modal
        show={showRetourModal}
        onClose={closeNewRetourModal}
        title="Nieuwe retour aanmelden"
        large
        contentClassName="modal-retour"
      >
        <div className="retour-modal-body">
          <p className="retour-modal-hint">
            Vul ordernummer in en tik op <strong>Zoek order</strong>. E-mail is in deze app <strong>optioneel</strong> (alleen nodig
            om te controleren of iemand tóch het juiste adres intikt). De webshop-klantflow zal straks wél ordernummer
            <strong> én e-mail</strong> vereisen.
          </p>
          <div className="retour-lookup-row">
            <div className="form-group">
              <label htmlFor="retour-ordernr">Ordernummer</label>
              <input
                id="retour-ordernr"
                type="text"
                inputMode="numeric"
                autoComplete="off"
                autoCorrect="off"
                value={retourLookupOrdernr}
                onChange={(e) => setRetourLookupOrdernr(e.target.value)}
                placeholder="bijv. 12345"
                disabled={retourLookupLoading || retourSaving}
              />
            </div>
            <div className="form-group">
              <label htmlFor="retour-email">E-mail (optioneel)</label>
              <input
                id="retour-email"
                type="email"
                inputMode="email"
                autoComplete="email"
                value={retourLookupEmail}
                onChange={(e) => setRetourLookupEmail(e.target.value)}
                placeholder="Leeg laten: alleen controle o.b.v. order"
                disabled={retourLookupLoading || retourSaving}
              />
            </div>
            <div className="retour-lookup-actions">
              <button
                type="button"
                className="btn btn-primary retour-lookup-btn"
                onClick={lookupRetourOrder}
                disabled={retourLookupLoading || retourSaving || !retourLookupOrdernr.trim()}
              >
                {retourLookupLoading ? 'Zoeken…' : 'Zoek order'}
              </button>
            </div>
          </div>

          {retourLookupError && (
            <div className="retour-error">{retourLookupError}</div>
          )}

          {retourOrder && (
            <div className="retour-order-info">
              {retourOrder.retour_termijn && !retourOrder.retour_termijn.allowed && (
                <div className="retour-termijn-waarschuwing" role="alert">
                  <strong>Klanttermijn verstreken</strong>
                  {' — '}
                  meer dan 30 dagen na geschatte ontvangst. Je kunt als admin wel doorgaan.
                  {retourOrder.retour_termijn.uiterste_datum && (
                    <>
                      {' '}
                      Uiterste datum voor klanten:{' '}
                      {String(retourOrder.retour_termijn.uiterste_datum).replace(
                        /^(\d{4})-(\d{2})-(\d{2}).*/,
                        '$3-$2-$1'
                      )}
                      .
                    </>
                  )}
                </div>
              )}
              <div className="retour-order-meta">
                <div>
                  <strong>Order #{retourOrder.order_nummer}</strong>
                  {retourOrder.datum && (
                    <span className="retour-order-datum">
                      {' '}— {formatRetourDatum(retourOrder.datum)}
                    </span>
                  )}
                </div>
                <div className="retour-order-klant">
                  {retourOrder.klant_naam} {retourOrder.klant_email && `— ${retourOrder.klant_email}`}
                </div>
                <div className="retour-order-betaling">
                  Betaalwijze: {retourOrder.payment_method_title || retourOrder.payment_method || '—'}
                  {' '}• Totaal: {formatEuro(retourOrder.totaal)}
                </div>
              </div>

              <div className="retour-items-table-wrap">
                <table className="retour-items-table retour-items-table--nieuw">
                  <thead>
                    <tr>
                      <th>Product</th>
                      <th>SKU</th>
                      <th>Prijs</th>
                      <th>Besteld</th>
                      <th>Retour</th>
                      <th>Subtotaal</th>
                    </tr>
                  </thead>
                  <tbody>
                    {(retourOrder.regels || []).map((r) => {
                      const aantal = retourSelected[r.wc_line_item_id] || 0;
                      return (
                        <tr key={r.wc_line_item_id}>
                          <td data-label="Product">{r.naam}</td>
                          <td className="retour-item-sku" data-label="SKU">
                            {r.sku || '—'}
                          </td>
                          <td data-label="Prijs stuk">{formatEuro(r.prijs_per_stuk)}</td>
                          <td className="retour-item-aantal" data-label="Besteld">
                            {r.aantal}
                          </td>
                          <td className="retour-item-input" data-label="Aantal retour">
                            <button
                              type="button"
                              className="retour-stepper-btn"
                              aria-label="Eén minder"
                              onClick={() => setRetourAantal(r.wc_line_item_id, aantal - 1)}
                              disabled={aantal <= 0 || retourSaving}
                            >
                              <FiMinus />
                            </button>
                            <input
                              type="number"
                              min={0}
                              max={r.aantal}
                              value={aantal}
                              onChange={(e) => setRetourAantal(r.wc_line_item_id, e.target.value)}
                              disabled={retourSaving}
                              className="retour-aantal-input"
                            />
                            <button
                              type="button"
                              className="retour-stepper-btn"
                              aria-label="Eén meer"
                              onClick={() => setRetourAantal(r.wc_line_item_id, Math.min(r.aantal, aantal + 1))}
                              disabled={aantal >= r.aantal || retourSaving}
                            >
                              <FiPlus />
                            </button>
                          </td>
                          <td data-label="Totaal regel">
                            {formatEuro(aantal * (r.prijs_per_stuk || 0))}
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                  <tfoot>
                    <tr className="retour-totaal-row">
                      <td colSpan={5} className="retour-totaal-label">
                        <strong>Totaal te crediteren</strong>
                      </td>
                      <td className="retour-totaal-bdr">
                        <strong>{formatEuro(retourTotaal)}</strong>
                      </td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              <div className="retour-form-fields">
                <div className="form-group">
                  <label htmlFor="retour-reden">Reden retour</label>
                  <select
                    id="retour-reden"
                    value={retourReason}
                    onChange={(e) => setRetourReason(e.target.value)}
                    disabled={retourSaving}
                  >
                    <option value="">— Kies een reden —</option>
                    {RETOUR_REDEN_OPTIES.map((o) => (
                      <option key={o.value} value={o.value}>
                        {o.label}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="form-group">
                  <label htmlFor="retour-klantnotitie">Toelichting van de klant</label>
                  <textarea
                    id="retour-klantnotitie"
                    rows={2}
                    value={retourCustomerNote}
                    onChange={(e) => setRetourCustomerNote(e.target.value)}
                    disabled={retourSaving}
                  />
                </div>
                <div className="form-group">
                  <label htmlFor="retour-internnotitie">Interne notitie</label>
                  <textarea
                    id="retour-internnotitie"
                    rows={2}
                    value={retourInternalNote}
                    onChange={(e) => setRetourInternalNote(e.target.value)}
                    disabled={retourSaving}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="modal-footer retour-modal-footer">
          <button
            type="button"
            className="btn btn-secondary"
            onClick={closeNewRetourModal}
            disabled={retourSaving}
          >
            Annuleren
          </button>
          <button
            type="button"
            className="btn btn-primary"
            onClick={submitRetour}
            disabled={retourSaving || !retourOrder || retourTotaal <= 0}
          >
            {retourSaving ? 'Opslaan…' : 'Retour aanmelden'}
          </button>
        </div>
      </Modal>

      <Modal
        show={!!retourDetail}
        onClose={closeRetourDetail}
        title={retourDetail ? `Retour ${retourDetail.retournummer}` : 'Retour'}
        large
        contentClassName="modal-retour"
      >
        {retourDetailLoading && <div>Laden…</div>}
        {retourDetail && (
          <div className="retour-detail-body">
            <div className="retour-detail-meta">
              <div>
                <strong>Order:</strong> {retourDetail.ordernummer}
              </div>
              <div>
                <strong>Klant:</strong> {retourDetail.klant_naam || '—'}
                {retourDetail.klant_postcode ? ` · ${retourDetail.klant_postcode}` : ''}
                {retourDetail.klant_email && ` — ${retourDetail.klant_email}`}
              </div>
              <div>
                <strong>Status:</strong>{' '}
                <span className={`retour-status-badge status-${retourDetail.status}`}>
                  {retourStatusLabel(retourDetail.status)}
                </span>
              </div>
              <div>
                <strong>Refund:</strong> {formatEuro(retourDetail.refund_bedrag)} (
                {retourRefundStatusLabel(retourDetail.refund_status)}
                {retourDetail.refund_status === 'failed' && retourDetail.refund_error
                  ? ` — ${retourDetail.refund_error}`
                  : ''}
                )
              </div>
              {retourDetail.reden && (
                <div>
                  <strong>Reden:</strong> {retourDetail.reden}
                </div>
              )}
              {retourDetail.opmerkingen_klant && (
                <div>
                  <strong>Toelichting klant:</strong> {retourDetail.opmerkingen_klant}
                </div>
              )}
              {retourDetail.opmerkingen_intern && (
                <div>
                  <strong>Intern:</strong> {retourDetail.opmerkingen_intern}
                </div>
              )}
            </div>

            {retourIsTerugbetaald(retourDetail) &&
            (retourDetail.regels || []).some(
              (rg) => (parseInt(rg.aantal_ok, 10) || 0) > 0 && rg.magazijn_locatie
            ) ? (
              <div className="retour-terugleg-banner">
                <strong>Terugleggen op locatie</strong>
                <ul className="retour-terugleg-lijst">
                  {(retourDetail.regels || [])
                    .filter((rg) => (parseInt(rg.aantal_ok, 10) || 0) > 0 && rg.magazijn_locatie)
                    .map((rg) => (
                      <li key={rg.id}>
                        <FiMapPin size={14} aria-hidden />
                        <span>
                          {rg.magazijn_locatie} — {rg.naam}
                          {(parseInt(rg.aantal_ok, 10) || 0) > 1
                            ? ` (${rg.aantal_ok}×)`
                            : ''}
                        </span>
                      </li>
                    ))}
                </ul>
              </div>
            ) : null}

            <div className="retour-items-table-wrap">
              <table className="retour-items-table retour-items-table--detail">
                <thead>
                  <tr>
                    <th>Product</th>
                    <th>SKU</th>
                    <th>Locatie</th>
                    <th>Aangem.</th>
                    <th>Ontv.</th>
                    <th>OK</th>
                    <th>Def.</th>
                    <th>Refund</th>
                  </tr>
                </thead>
                <tbody>
                  {(retourDetail.regels || []).map((r) => (
                    <tr key={r.id}>
                      <td data-label="Product">{r.naam}</td>
                      <td className="retour-item-sku" data-label="SKU">
                        {r.sku || '—'}
                      </td>
                      <td className="retour-item-locatie" data-label="Locatie">
                        {r.magazijn_locatie?.trim() ? (
                          <strong>{r.magazijn_locatie}</strong>
                        ) : (
                          '—'
                        )}
                      </td>
                      <td data-label="Aangemeld">{r.aantal_aangemeld}</td>
                      <td data-label="Ontvangen">{r.aantal_ontvangen}</td>
                      <td data-label="OK">{r.aantal_ok}</td>
                      <td data-label="Defect">{r.aantal_defect}</td>
                      <td data-label="Refund">{formatEuro(r.totaal_refund)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="retour-detail-actions" id="retour-detail-terugbetaling-acties">
              {retourDetail.status !== 'geweigerd' &&
                retourDetail.status !== 'terugbetaald' &&
                retourDetail.refund_status !== 'succeeded' && (
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => {
                      const id = retourDetail.id;
                      closeRetourDetail();
                      openRetourOntvangst(id);
                    }}
                  >
                    Open ontvangst (scan)
                  </button>
                )}
              {retourDetail.status === 'aangemeld' && (
                <>
                  <button
                    type="button"
                    className="btn btn-primary"
                    onClick={() => updateRetourStatus(retourDetail.id, 'ontvangen')}
                  >
                    Alleen status: ontvangen
                  </button>
                  <button
                    type="button"
                    className="btn btn-secondary"
                    onClick={() => updateRetourStatus(retourDetail.id, 'geweigerd')}
                  >
                    Weiger retour
                  </button>
                </>
              )}
              {['aangemeld', 'ontvangen'].includes(retourDetail.status) &&
                retourDetail.refund_status !== 'succeeded' &&
                retourDetail.refund_status !== 'bol_handmatig' && (
                <button
                  type="button"
                  className="btn btn-primary"
                  disabled={!retourKlaarVoorTerugbetaling || retourTerugbetalenLaden}
                  title={
                    !retourKlaarVoorTerugbetaling
                      ? 'Registreer eerst minimaal één ontvangen stuk (OK of defect). Gedeeltelijke retouren: alleen ontvangen producten worden terugbetaald.'
                      : retourAllRegelsInOntvangst
                        ? 'MultiSafePay: terugbetaling via Woo + restock. Bol.com: Woo-refund zonder gateway (restock) + ticket voor handmatige klant-terugbetaling via Bol.'
                        : 'Gedeeltelijke terugbetaling: alleen ontvangen producten. Woo-order blijft op Afgerond tenzij het volledige orderbedrag is terugbetaald.'
                  }
                  onClick={() => terugbetalenRetour(retourDetail.id)}
                >
                  {retourTerugbetalenLaden ? 'Bezig met terugbetalen…' : 'Goedkeuren & terugbetalen'}
                </button>
              )}
              {retourDetail.refund_status !== 'succeeded' && retourDetail.refund_status !== 'bol_handmatig' && (
                <button
                  type="button"
                  className="btn btn-danger"
                  onClick={() => deleteRetour(retourDetail.id)}
                >
                  Verwijderen
                </button>
              )}
              <button type="button" className="btn btn-secondary" onClick={closeRetourDetail}>
                Sluiten
              </button>
            </div>
          </div>
        )}
      </Modal>

      <Modal
        show={!!retourOntvangstDetail}
        onClose={() => !retourOntvangstLaden && closeRetourOntvangst()}
        title={retourOntvangstDetail ? `Ontvangst — ${retourOntvangstDetail.retournummer || ''}` : 'Ontvangst'}
        large
        contentClassName="modal-retour"
      >
        {retourOntvangstDetail && (
          <div className="retour-ontvangst-body">
            <p className="retour-ontvangst-instructie">
              Kies <strong>In orde</strong> of <strong>Defect</strong> — dat geldt voor <strong>scannen</strong> en voor het <strong>vinkje</strong> per regel. Scan of type EAN/SKU per stuk, of vink
              <em> Resterend volledig ontvangen</em> zonder iets in te toetsen. Bij dezelfde EAN in meerdere regels
              pakt de scan eerst de regel met het meeste restant. <strong>Let op:</strong> de voorraad in de webshop
              (WooCommerce) stijgt pas weer na <strong>terugbetaling</strong> van de retour, niet bij ontvangst in het magazijn.
            </p>

            <div className="retour-ontvangst-conditie" role="group" aria-label="Productconditie">
              <button
                type="button"
                className={`retour-conditie-btn ${ontvangstConditie === 'ok' ? 'active' : ''}`}
                onClick={() => setOntvangstConditie('ok')}
                disabled={retourOntvangstLaden}
              >
                <FiCheck /> In orde
              </button>
              <button
                type="button"
                className={`retour-conditie-btn ${ontvangstConditie === 'defect' ? 'active' : ''}`}
                onClick={() => setOntvangstConditie('defect')}
                disabled={retourOntvangstLaden}
              >
                Defect
              </button>
            </div>

            <div className="retour-ontvangst-scanrow">
              <input
                ref={ontvangstInputRef}
                type="text"
                className="retour-ontvangst-input"
                placeholder="EAN of SKU (handmatig of scan)"
                value={ontvangstCode}
                onChange={(e) => setOntvangstCode(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') {
                    e.preventDefault();
                    doRetourOntvangstScan(ontvangstCode);
                  }
                }}
                disabled={retourOntvangstLaden}
                autoComplete="off"
                autoCorrect="off"
                spellCheck={false}
              />
              <button
                type="button"
                className="btn btn-secondary retour-ontvangst-cam"
                onClick={() => {
                  setScanContext(MAGAZIJN_RETOUR_ONTVANGST);
                  setShowScanner(true);
                }}
                disabled={retourOntvangstLaden}
                title="Camera"
              >
                <FiCamera />
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => doRetourOntvangstScan(ontvangstCode)}
                disabled={retourOntvangstLaden || !ontvangstCode.trim()}
              >
                {retourOntvangstLaden ? 'Bezig…' : 'Toevoegen'}
              </button>
            </div>

            {ontvangstError && <div className="retour-error retour-ontvangst-feedback err">{ontvangstError}</div>}
            {ontvangstMsg && <div className="retour-ontvangst-feedback ok">{ontvangstMsg}</div>}

            <ul className="retour-ontvangst-regels">
              {(retourOntvangstDetail.regels || []).map((rg) => {
                const rest = retourToontvangstRest(rg);
                const aang = parseInt(rg.aantal_aangemeld, 10) || 0;
                const gedaan = aang - rest;
                return (
                  <li key={rg.id} className="retour-ontvangst-regel">
                    <div className="retour-ontvangst-regel-top">
                      <span className="retour-ontvangst-regel-naam">{rg.naam}</span>
                      {typeof rg.koppel_ok === 'boolean' && !rg.koppel_ok && (
                        <span className="retour-ontvangst-waarschuwing" title="Geen inkoop-artikel: scan werkt vaak via EAN/SKU op het product, of volledig ontvangen.">
                          (niet gekoppeld aan inkoop-artikel)
                        </span>
                      )}
                    </div>
                    <div className="retour-ontvangst-regel-meta">
                      {rg.sku && <span>SKU: {rg.sku}</span>}
                      {rg.magazijn_locatie != null && String(rg.magazijn_locatie) !== '' && (
                        <span>Loc: {rg.magazijn_locatie}</span>
                      )}
                    </div>
                    <div className="retour-ontvangst-progress" aria-hidden>
                      <div
                        className="retour-ontvangst-progress-bar"
                        style={{ width: `${aang > 0 ? (gedaan / aang) * 100 : 0}%` }}
                      />
                    </div>
                    <div className="retour-ontvangst-regel-stats">
                      OK: {parseInt(rg.aantal_ok, 10) || 0} · Defect: {parseInt(rg.aantal_defect, 10) || 0} ·
                      Resterend: {rest} / {aang}
                    </div>
                    {rest > 0 && (
                      <label
                        className={`retour-ontvangst-vink ${ontvangstRegelLaden === rg.id ? 'loading' : ''}`}
                        key={`vink-${rg.id}-${rest}`}
                      >
                        <input
                          type="checkbox"
                          disabled={retourOntvangstLaden || ontvangstRegelLaden != null}
                          title="Boek het resterend aantal in één keer (volgens In orde / Defect hierboven). Woo-voorraad volgt na terugbetaling."
                          onChange={async (e) => {
                            if (!e.target.checked) return;
                            const ok = await doRetourVolledigeRegel(rg);
                            if (!ok) e.target.checked = false;
                          }}
                        />
                        <span>
                          Resterend <strong>volledig ontvangen</strong> (
                          {ontvangstConditie === 'ok' ? 'in orde' : 'defect'}) — <strong>{rest} stuk(s)</strong> tegelijk
                        </span>
                      </label>
                    )}
                  </li>
                );
              })}
            </ul>

            <div className="retour-ontvangst-footer-btns">
              <button
                type="button"
                className="btn btn-primary"
                disabled={retourOntvangstLaden || !ontvangstNaarTerugbetaling.mag}
                title={ontvangstNaarTerugbetaling.title}
                onClick={async () => {
                  const id = ontvangstNaarTerugbetaling.retourId;
                  if (!id) return;
                  closeRetourOntvangst();
                  await openRetourDetail(id);
                  setTimeout(() => {
                    document
                      .getElementById('retour-detail-terugbetaling-acties')
                      ?.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
                  }, 150);
                }}
              >
                Naar terugbetaling
              </button>
              <button type="button" className="btn btn-secondary" onClick={closeRetourOntvangst} disabled={retourOntvangstLaden}>
                Sluiten
              </button>
            </div>
          </div>
        )}
      </Modal>
      </div>
      {selectedProduct ? (
        <div className="product-label-sheet" aria-hidden="true">
          <div className="product-label-sheet-inner">
            <h2 className="product-label-title">{selectedProduct.naam}</h2>
            <p className="product-label-code-line">
              {selectedProduct.ean ? (
                <span className="product-label-ean">EAN: {selectedProduct.ean}</span>
              ) : null}
              {selectedProduct.ean && selectedProduct.sku ? <br /> : null}
              {selectedProduct.sku ? <span className="product-label-sku">SKU: {selectedProduct.sku}</span> : null}
              {!selectedProduct.ean && !selectedProduct.sku ? <>—</> : null}
            </p>
            <svg ref={labelSvgRef} className="product-label-barcode" />
            <p className="product-label-tagline">100% handmade with love</p>
          </div>
        </div>
      ) : null}
    </div>
  );
};

export default Magazijn;
